/**
 * S14 Fleet — the neighbourhood reading, reworked. Backlog tasks 81, 82, and the 2026-08-05 UX pass.
 *
 * `UI-Design-Spec.md#s14--fleet` specifies nine columns and four interactions, and
 * `#reading-168-rows` specifies the family view that makes `Fleet-Selection-Rule.md` §8 executable.
 * Both are rendered here. The overlay-five-on-one-axis interaction, bulk-add from a sweep, and
 * promote-into-J3 remain phase 2.
 *
 * ## 🔴 What the 2026-08-05 rework changed, and why none of it is taste
 *
 * The grouping shipped correct and unreadable. Six defects, each **measured in the running app**
 * rather than argued from the code, and each one degrading the §8 reading specifically:
 *
 * | Measured | Was | Now |
 * |---|---|---|
 * | Hero value | **15px** — `--num-md`, because `Num` sets its own size and overrode `MetricTile`'s | `--num-display`, which is what the spec asked for |
 * | `⧗` `✓` `⚠` | **font-substituted**; identical advance widths in Geist and in a nonexistent family | drawn — see `FleetMarks.tsx` |
 * | Glyph run | **13px**, the smallest thing in its own panel | `--num-lg`, and bracketed |
 * | Family table | 1552px wide, `Supported ✓` alone **233px** for one digit | four fixed columns, ~960px, counts in a 200px cluster |
 * | Parameter values | 11px `--text-tertiary` | 12px `--text-secondary` |
 * | Six count columns | literal `0` on 9 of 10 rows | a silent `·`, so the eye lands on what is not zero |
 *
 * The last two rows of that table are one rule each from documents this screen is downstream of.
 * `colors.css` states its own: *"`--text-tertiary` remains labels-at-12px-and-up only, never a
 * value"* — and the parameter under each glyph is a value, at 11px, in tertiary. And a `0` in a
 * count column is not a measurement of zero; it is the absence of a thing to count, which the
 * screen already renders as `—` when the API says so.
 *
 * ⚠ **Why the family view is no longer a `DataTable`.** `DataTable` is `width: 100%` with the
 * browser's automatic layout and no per-column width, so nine columns of one digit each get the
 * viewport divided among them — which is what put ~1000px between the seven numbers describing one
 * family. It cannot be fixed in the component: `web/src/design/` is vendored under a checksum
 * manifest (`ops/design_manifest.py`) and an edit there is a lie about provenance. So the family
 * view is a `table-layout: fixed` table owned by this screen, and the flat view keeps `DataTable` —
 * which is the right component for it, because nine per-candidate measurements genuinely are a
 * table and its columns genuinely do want the width.
 *
 * ## The reading itself is unchanged, and three properties of it are load-bearing
 *
 * - **The marks are an inline run in parameter order.** Three adjacent passes is a neighbourhood
 *   and one pass between two failures is not, and the difference must be visible without counting.
 *   🔴 **The rework draws that**: a bracket spans the longest passing run, from
 *   `longestPassSpan` — the same function `readingOf` derives its verdict from, so the bracket and
 *   the label cannot disagree.
 * - **An isolated pass renders as §8's `(✓)` and drained of colour**, never as a win. §8 calls it a
 *   non-result and §3 would promote it.
 * - **Nothing is ever sorted globally by a metric** on the family view. A global sort destroys the
 *   adjacency the reading depends on. Families stay in the API's order — the seeder's declaration
 *   order — so a family is in the same place every morning.
 *
 * ✅ **Filtering is added and sorting is still refused, which is not a loophole.** §8 prohibits
 * reordering, because adjacency is positional. A filter removes whole families and never touches
 * the order of points within a run, so `Neighbourhood` / `Flagged` / `Isolated only` is triage that
 * costs the reading nothing — and at 7 families of 168 candidates, triage is the morning.
 *
 * ⚠ Every metric is nullable in the API for one reason — *"not computable yet"* — and this screen
 * renders that as an em dash. A candidate four bars old has no Sharpe, and `0.00` in that cell
 * would be a number where there is no measurement, on the screen whose job is deciding which
 * candidate gets real money.
 */

import { useQuery } from '@tanstack/react-query';
import { Fragment, type JSX, useEffect, useMemo, useState } from 'react';

import { api } from '../api/client';
import type { FleetCandidate, FleetPosition } from '../api/generated';
import type { DataTableColumn, DataTableRow } from '../design/components/data/DataTable';
import { DataTable } from '../design/components/data/DataTable.jsx';
import { MetricTile } from '../design/components/data/MetricTile.jsx';
import { Num } from '../design/components/data/Num.jsx';
import { EquityPlot } from '../design/components/data/EquityPlot.jsx';
import { Sparkline } from '../design/components/data/Sparkline.jsx';
import { StatePill } from '../design/components/data/StatePill.jsx';
import { EmptyState } from '../design/components/messages/EmptyState.jsx';
import { ErrorPanel } from '../design/components/messages/ErrorPanel.jsx';
import { Skeleton } from '../design/components/messages/Skeleton.jsx';
import { Modal } from '../design/components/overlays/Modal.jsx';
import { TabBar } from '../design/components/chrome/TabBar.jsx';
import type { DimensionRun, FamilyGroup, SweepPoint } from './FleetGrouping';
import { PASS, groupFleet, longestPassSpan, runsFor } from './FleetGrouping';
import {
  type Mark,
  SigMark,
  StateMark,
  closeBreakdown,
  exitReasonFill,
  markFor,
} from './FleetMarks';
import { CoinMark } from '../ui/CoinMark';
import { readFleetView } from '../ui/preferences';
import { type TimestampMode, ageOf, clockOf, useTimestampMode } from '../ui/timestamps';
import { useElementWidth } from '../ui/useElementWidth';
import { useIsPhone } from '../ui/useIsPhone';

/**
 * An em dash, for the cells `Num` does not render.
 *
 * ✅ **`Num` already does this for every metric**, and that is why it is used rather than a local
 * formatter: its own contract is *"null / undefined renders an em dash in --text-tertiary. Never 0,
 * never blank."* The design system had already decided that a missing measurement must look
 * different from a zero one, so this screen inherits the rule instead of restating it.
 */
const NONE = '—';

/** The detail sheet's four panes. */
type DetailPane = 'measurements' | 'parameters' | 'positions' | 'fills';

/**
 * The sheet's tabs.
 *
 * ⚠ **`Measurements` opens, and that is not arbitrary.** The four figures that decide a promotion
 * live there; the parameters are what you consult once a measurement has made you curious. Opening
 * on parameters would put the answer behind a click and the question in front of it.
 */
const DETAIL_PANES: ReadonlyArray<{ id: DetailPane; label: string; short: string }> = [
  { id: 'measurements', label: 'Measurements', short: 'Metrics' },
  { id: 'parameters', label: 'Parameters', short: 'Params' },
  // 🔴 **`Positions` sits BEFORE `Executions`, and the order is the reading order — S14 task 3.**
  // A position is the unit a strategy reasons in; a fill is the unit a venue reasons in, and there
  // are two or more of them per position. Putting executions first would open on the finer grain
  // and make the coarser one look like a summary of it, when it is the other way round.
  { id: 'positions', label: 'Positions', short: 'Positions' },
  // ⚠ **`Executions`, not `Trades`.** `row.trades` on this screen is the count of completed round
  // trips; this pane lists the individual buys and sells, of which a round trip is two or more.
  // Reusing the word would make one screen use it for two different counts.
  // ⚠ `Fills` on a phone. The full label is `Executions` for the reason above, and `Fills` is the
  // API's own word for the same rows — not `Trades`, which is the count this screen uses for round
  // trips. Abbreviating rather than truncating, exactly as the table header does with `Sig` and `30d`.
  { id: 'fills', label: 'Executions', short: 'Fills' },
];

/** Narrows a `TabBar` id back to the union, so a new pane cannot silently fall through. */
function toPane(id: string): DetailPane {
  return DETAIL_PANES.some((pane) => pane.id === id) ? (id as DetailPane) : 'measurements';
}

/**
 * `Sparkline` takes `number[]`, and equity arrives as a decimal string (H6).
 *
 * ⚠ **This is the one place a `Number()` on money is correct, and it needs the argument stated
 * rather than assumed.** `api/client.ts` says plainly: *"Money is a string (H6). Do not `Number()`
 * one."* That rule is about **arithmetic** — a price that loses its last digits produces a wrong
 * order size. This is a 64×24 pixel shape whose whole output is a path, so the float is the
 * rendering target rather than an intermediate: every value is scaled to the plot's own range and
 * nothing computed from it survives the draw.
 */
function toPlot(points: string[]): number[] {
  return points.map((value) => Number(value));
}

/**
 * Which of the five tones a significance state renders in.
 *
 * ⚠ **No amber anywhere**, per S14's own rule — *"no mode markers anywhere, no amber, no blue"* —
 * because amber means real money and nothing on this screen has any. `flagged` takes `--loss`
 * instead, which is the same message without borrowing the vocabulary of a live alert.
 */
/**
 * What the `Excess` cell's tooltip says — which control this row was measured against, and what it
 * returned. D73.
 *
 * 🔴 **The bar differs per row, so the number cannot be read without it.** `−1.4%` says nothing
 * on its own: a long is measured against buy-and-hold, a short against sell-and-hold, and a
 * market-neutral book against zero. Measured on the live universe 2026-08-17 — 58 of 74 instruments
 * in a downtrend — the three bars are nowhere near each other.
 *
 * ⚠ **A missing control is stated, not rendered as zero.** `control_pct` is null when the cohort's
 * control for this direction has no equity yet, and the row is `not_ranked` for that reason rather
 * than for anything about the candidate.
 */
function excessTitle(row: FleetCandidate): string {
  const against =
    row.direction === 'neutral'
      ? 'a flat book (0%) — a market-neutral book’s expected return absent skill is ~0'
      : `${row.direction === 'short' ? 'sell' : 'buy'}-and-hold on this rung`;
  if (row.control_pct === null) {
    return `No bar yet: the ${row.direction} control has no equity, so this cannot be ranked.`;
  }
  return (
    `Net return in excess of ${against}, which returned ${Number(row.control_pct).toFixed(2)}%. ` +
    'This is the number the promotion is decided on — not the raw return beside it.'
  );
}

function toneOf(significance: string, supported: boolean): string {
  if (significance !== PASS) return significance === 'flagged' ? 'flagged' : 'quiet';
  return supported ? 'pass' : 'isolated';
}

/**
 * `Families` first, and it is the default because §8's reading is the morning question.
 *
 * *"Seven rows is the morning read, and it preserves the hero's 'n promotable of 168'."* The flat
 * table is the second segment rather than the first — it answers a per-candidate question, which is
 * the one asked after a neighbourhood has been found rather than before.
 */
const VIEWS = [
  {
    value: 'families',
    label: 'Families',
    title: "§8's reading: one row per family, expanding to its swept dimensions",
  },
  { value: 'rows', label: 'All rows', title: 'Every candidate, with the nine per-candidate columns' },
  {
    value: 'executions',
    label: 'Executions',
    title: 'Every buy and sell the fleet has made, newest first, searchable',
  },
];

/**
 * The four readings a family can have, as a chip.
 *
 * 🔴 **The ladder is evidence, not attention, and the order matters.** `ISOLATED ONLY` sits *below*
 * `TWO ADJACENT` because §8 makes an isolated pass a non-result — so a family whose only passes are
 * isolated must read as weaker than one with two adjacent, even though it has the same number of
 * `promotable` candidates. That inversion is the whole rule, and a chip that ranked by pass count
 * would quietly undo it.
 *
 * ⚠ **A flag is deliberately not in this ladder.** A flagged candidate is a bug signal rather than
 * an evidence state, and it already has its own mark and count in the evidence cluster. Letting it
 * win this column would replace the answer to *"is there evidence here"* with the answer to a
 * different question.
 */
const NO_PASS = { label: 'No pass', tone: 'none', note: '' };

const READINGS: Readonly<Record<string, { label: string; tone: string; note: string }>> = {
  neighbourhood: { label: 'Neighbourhood', tone: 'pass', note: 'three adjacent passes' },
  pair: { label: 'Two adjacent', tone: 'quiet', note: 'not a neighbourhood' },
  isolated: { label: 'Isolated', tone: 'isolated', note: 'a non-result under §8' },
  none: NO_PASS,
};

/**
 * Which chip a family's counts earn it. Derived from the counts, so it invents no judgement.
 *
 * ⚠ **`Isolated only` rather than `Isolated`** on a family: a run has one reading, but a family is
 * a collection of runs, and the word that matters is that it holds *nothing else*.
 */
function readingOfFamily(group: FamilyGroup): { label: string; tone: string } {
  if (group.neighbourhoods > 0) return READINGS.neighbourhood ?? NO_PASS;
  if (group.supported > 0) return READINGS.pair ?? NO_PASS;
  if (group.isolated > 0) return { label: 'Isolated only', tone: 'isolated' };
  return NO_PASS;
}

/** The filter segments. Counts are appended live, so a segment with nothing behind it says so. */
const FILTERS: ReadonlyArray<{
  value: string;
  label: string;
  title: string;
  keep: (group: FamilyGroup) => boolean;
}> = [
  { value: 'all', label: 'All', title: 'Every family, in the seeder’s declaration order', keep: () => true },
  {
    value: 'neighbourhood',
    label: 'Neighbourhood',
    title: 'Families with at least one dimension passing at three adjacent points',
    keep: (group) => group.neighbourhoods > 0,
  },
  {
    value: 'flagged',
    label: 'Flagged',
    title: 'Families holding a candidate that diverges from its own backtest',
    keep: (group) => group.flagged > 0,
  },
  {
    value: 'isolated',
    label: 'Isolated only',
    title: 'Families whose only passes are isolated — §8 reads these as non-results',
    keep: (group) => group.isolated > 0 && group.neighbourhoods === 0 && group.supported === 0,
  },
];

export function FleetScreen(): JSX.Element {
  // ⚠ **Read once, at mount.** The preference decides what this screen *opens* on; it is not a
  // controlled binding, so switching view here is a look at something rather than a change of
  // mind about the default. Settings is where the default moves.
  const [view, setView] = useState<string>(() => readFleetView());
  const [filter, setFilter] = useState('all');

  const fleet = useQuery({
    queryKey: ['fleet'],
    queryFn: () => api.fleet.listFleet(),
  });

  // 🔴 Grouped once, here, above the early returns — because the filter's per-segment counts and
  // the rows it filters must come from the same reading. Two `groupFleet` calls would be two
  // chances for a segment to promise a family the table then does not show.
  const candidates = fleet.data?.candidates;
  const groups = useMemo(() => groupFleet(candidates ?? []), [candidates]);


  if (fleet.isLoading) {
    return (
      <div className="jmt-screen">
        <Skeleton count={8} />
      </div>
    );
  }
  if (fleet.isError) {
    return (
      <div className="jmt-screen">
        <ErrorPanel
          message="Could not load the fleet."
          detail={String((fleet.error as Error)?.message ?? '')}
          onRetry={() => void fleet.refetch()}
        />
      </div>
    );
  }

  const data = fleet.data;
  const items: FleetCandidate[] = data?.candidates ?? [];
  if (items.length === 0) {
    return (
      <div className="jmt-screen">
        <div className="jmt-note">
          <EmptyState>
            No candidates. The fleet is 8&ndash;10 strategies running in shadow, seeded by hand
            &mdash; backlog task 80, which starts a four-to-six week evidence clock and is
            deliberately a decision rather than a button. Until it runs there is nothing to rank.
          </EmptyState>
        </div>
      </div>
    );
  }

  return (
    <div className="jmt-screen">
      {/* The hero, and the only one on this screen, with the rest of the cohort's shape beside it
          rather than crushed into one nowrap line under it. `of {total}` stays on the hero itself,
          because "6" answers nothing without the denominator the gate is drawn from. */}
      <header className="jmt-fleet__head jmt-panel">
        <MetricTile
          variant="hero"
          label="Promotable"
          // 🔴 `step="display"` is the fix, and the defect it fixes is worth naming. `MetricTile`
          // sizes a hero by setting `font-size` on the wrapper; `Num` sets its own, and `md` (15px)
          // won. So the spec's *"Hero: promotable count, `--num-display`"* rendered three type
          // steps below a caption. It survived five sessions because `ops/screenshot_ui.py`
          // photographs this screen's EMPTY state — its own docstring says so — and the populated
          // one only exists under Vite at `/__design/screens`.
          value={<Num value={data?.promotable ?? 0} dp={0} step="display" />}
          // 🔴 **The unresolved count rides on the HERO's own context line — D73.** Shorts are
          // promotable now, so this number can include candidates whose short leg has no venue and
          // whose promotion would raise Box 1. Putting the qualification anywhere else lets the one
          // number the screen exists to answer read as *"ready to fund"* when part of it is not; on
          // the hero's context it cannot be seen without the caveat. Omitted entirely at zero, so
          // the usual case stays a clean denominator.
          context={
            (data?.promotable_unresolved ?? 0) > 0
              ? `of ${data?.total ?? 0} candidates · ${data?.promotable_unresolved} need the venue and Box 1 decisions first`
              : `of ${data?.total ?? 0} candidates`
          }
          // 🔴 **One line, and the long version was 102px of the panel's 253.** `MetricTile` renders
          // `translation` as an inline `<div>` capped at `38ch`, so a 340-character string wraps to
          // six lines of tertiary text under the hero — measured, and it made this panel taller than
          // four table rows for prose nobody reads twice. The load-bearing half is the D73 caveat,
          // and the `context` line beside it already counts the candidates it applies to.
          translation="Eligible for the gate, not funded."
        />
        <dl className="jmt-fleet__rail">
          <FleetStat label="Flagged" value={data?.flagged ?? 0} tone={(data?.flagged ?? 0) > 0 ? 'flagged' : 'quiet'} />
          <FleetStat label="Days" value={data?.days ?? 0} tone="quiet" />
          <FleetStat
            label="Capacity"
            value={`${data?.total ?? 0}/${data?.capacity ?? 0}`}
            tone="quiet"
          />
          {/* 🔴 **Net versus gross — cohort 4 §7, and it is the Box 1 tripwire.**
              §4 named `max_net_exposure` as the check the tax boundary depends on. Measured, that is
              backwards: the check caps |net| from *above*, while the Dutch boundary is that
              directional trading stays Box 3 and **market-neutral tips into Box 1** — net near
              **zero**. So no value of that check warns about it, and this readout is what does.
              ⚠ **Low is the warning.** 100% is purely directional and safe on that axis; a number
              falling toward 0 is the fleet approaching market-neutral. Hence the `flagged` tone
              below a threshold rather than above one — the inverse of every other stat here. */}
          <FleetStat
            label="Directional"
            value={data ? `${Number(data.directional_pct).toFixed(0)}%` : NONE}
            tone={
              data && Number(data.gross_exposure) > 0 && Number(data.directional_pct) < 50
                ? 'flagged'
                : 'quiet'
            }
            title={
              data
                ? `Net ${money(data.net_exposure)} of gross ${money(data.gross_exposure)}. ` +
                  '100% is purely directional; a number approaching 0% means the fleet is ' +
                  'market-neutral, which is the Box 1 boundary rather than a reassurance.'
                : undefined
            }
          />
        </dl>
      </header>


      {/* 🔴 **The permanent caution, and it updates with the count.** *"It states the count and the
          implication."* This is `CautionBand` — the design system's own anti-overfitting device,
          which the spec names for exactly this job (*"the same discipline as the sweep-mode caution
          in S5"*) and which this screen had been hand-rolling as an 11px grey paragraph.

          No arithmetic is put on it: §8 pins the best-of-N inflation at 3.2σ for this cohort but
          nothing in the rule fixes an expected number of lucky candidates, and a screen inventing
          one would be doing exactly what it warns against. */}
      <p className="jmt-caution" role="note">
        <strong>{data?.total ?? 0} candidates running.</strong> With a cohort this size some will
        look excellent by chance, which is why the reading below is by neighbourhood and not by
        row.
      </p>

      <div className="jmt-fleet__toolbar">
        {/* 🔴 **App-owned, because the vendored `SegmentedControl`'s thumb desynced from its own
            labels.** That component paints its active state with an absolutely positioned 1px span
            transformed by `translateX(offsetLeft) scaleX(offsetWidth)`, measured in a layout effect
            and animated on `transform`. The operator caught it with the fill sitting under `All
            rows` while `Families` carried the active text: two elements disagreeing about which
            segment is selected, on the control that decides what the screen shows.

            I patched around this twice — an inset focus ring, then `overflow: hidden` on the track
            — and both were treating a symptom, because the defect is the mechanism. Two buttons
            with a CSS `background` cannot disagree with their own labels: there is nothing to
            measure, nothing to animate, and no second element holding a stale position. */}
        <div className="jmt-views" role="group" aria-label="View">
          {VIEWS.map((option) => (
            <button
              key={option.value}
              type="button"
              className="jmt-view"
              data-on={view === option.value ? '' : undefined}
              aria-pressed={view === option.value}
              title={option.title}
              onClick={() => setView(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {/* ⚠ **Chips, not a second segmented control.** That control measured 425px beside the
            167px View control, which is what read as cluttered. A segmented track is the right
            shape for two mutually exclusive views of equal weight; a filter is subordinate to the
            view it filters, and it wraps. */}
        {view === 'families' ? (
          <div className="jmt-filters" role="group" aria-label="Show">
            <span className="jmt-filters__label">Show</span>
            {FILTERS.map((f) => {
              const n = groups.filter(f.keep).length;
              return (
                <button
                  key={f.value}
                  type="button"
                  className="jmt-filter"
                  data-on={filter === f.value ? '' : undefined}
                  aria-pressed={filter === f.value}
                  // A filter with nothing behind it cannot be pressed. `All` is never disabled,
                  // because a fleet with no families is an empty state rather than a filter.
                  disabled={n === 0 && f.value !== 'all'}
                  title={f.title}
                  onClick={() => setFilter(f.value)}
                >
                  {f.label}
                  <span className="jmt-filter__n">{n}</span>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* The table sits in a flush panel: a lifted surface with no padding of its own, because
          the table draws its own cell padding and doubling them puts the first column 44px from
          an edge it should sit 24px from.

          ⚠ **The measure is on the panel, not the table**, and the first attempt had it the other
          way round. Capping the table left the panel at full width, so a 50px sliver of empty
          lifted surface sat to the right of the last column — a table not filling its own
          container, which is exactly the kind of not-quite-aligned edge that makes a screen feel
          unfinished. Cap the container and the table fills it by definition. */}
      <div
        className="jmt-panel jmt-panel--flush"
        data-measured={view === 'rows' ? '' : undefined}
      >
        {view === 'families' ? (
          <FamilyView groups={groups} filter={filter} />
        ) : view === 'executions' ? (
          <ExecutionsView />
        ) : (
          <RowView items={items} />
        )}
      </div>

      <FleetLegend />
    </div>
  );
}

/**
 * 🔴 **Every execution the fleet has made, in one list.**
 *
 * `Families` answers *"where is the evidence"* and `All rows` *"how is each candidate doing"*.
 * Neither answers *"what has actually been traded"* — and that question is asked from the other
 * direction: not *how did candidate 297 do*, but *who has been touching ADA/EUR, and when*. The
 * per-candidate pane cannot serve it, because it needs the candidate chosen first.
 *
 * ⚠ **The search is server-side, not a filter over a loaded page.** Filtering in the browser would
 * search the most recent 300 rows and quietly report nothing for a candidate that traded on
 * Tuesday — a search box that looks like it works and answers about a window the operator cannot
 * see. `q` goes to the query, and `total` is the count of *matches* rather than of the page.
 */
function ExecutionsView(): JSX.Element {
  const phone = useIsPhone();
  const [term, setTerm] = useState('');
  // ⚠ **Debounced, and 250ms is chosen rather than default.** Every keystroke is a query across
  // every fill this account owns; unthrottled, typing `ADA` is three scans of the table. Short
  // enough that it still feels like typing into a live list.
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(term), 250);
    return () => clearTimeout(timer);
  }, [term]);

  const history = useQuery({
    queryKey: ['fleet', 'history', query],
    queryFn: () => api.fleet.listFleetHistory({ q: query || undefined }),
    // The previous page stays on screen while the next one is fetched, so a search does not blink
    // the table out and back — the row count changing under a steady list is the feedback.
    placeholderData: (previous) => previous,
  });

  const fills = history.data?.fills ?? [];
  const total = history.data?.total ?? 0;
  const returned = history.data?.returned ?? 0;

  return (
    <div className="jmt-history">
      <div className="jmt-history__bar">
        <input
          type="search"
          className="jmt-history__search"
          placeholder="Filter by candidate or instrument"
          aria-label="Filter executions by candidate or instrument"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
        />
        {/* ⚠ Says how many MATCHED, not how many are shown, and says both when they differ. A
            count that silently meant "the page" would make a truncated list read as a complete
            answer — the same rule as the detail sheet's footer. */}
        <span className="jmt-history__count">
          {history.isLoading
            ? '—'
            : total === returned
              ? `${total} execution${total === 1 ? '' : 's'}`
              : `${returned} of ${total} executions`}
        </span>
      </div>

      {history.isError ? (
        <ErrorPanel message="The execution history could not be loaded." />
      ) : history.isLoading ? (
        <Skeleton />
      ) : (
        <DataTable
          density="compact"
          columns={historyColumns(phone)}
          rows={fills.map((fill, index) => ({ ...fill, id: index }))}
          empty={
            query
              ? `Nothing matches “${query}”. The search covers candidate names and instruments.`
              : 'The fleet has executed nothing yet.'
          }
        />
      )}
    </div>
  );
}

/**
 * The history table's columns.
 *
 * ⚠ **`Candidate` is first and the per-candidate table's columns are not repeated.** This list is
 * read by scanning down one column at a time — *which candidate*, *which instrument* — so the two
 * identifying columns lead and the money follows.
 */
function historyColumns(phone: boolean): DataTableColumn[] {
  return [
    {
      key: 'ts',
      label: 'When',
      mono: true,
      render: (row: DataTableRow) => {
        const full = clockOf(String(row.ts), 'local');
        return phone ? full.slice(-5) : full;
      },
    },
    {
      key: 'candidate',
      label: 'Candidate',
      // 🔴 **The rung rides with the name, because without it the name is ambiguous by design.**
      // `DipBuyer` at 1h and `DipBuyer` at 4h are two different candidates sharing one strategy,
      // and the fleet runs both — so a Candidate column alone cannot tell you which row you are
      // looking at. Same `name / meta` shape the flat table already uses, rather than a second
      // column, so the two tables name a candidate the same way.
      render: (row: DataTableRow) => (
        <span className="jmt-history__candidate">
          <span className="jmt-history__name">{String(row.candidate)}</span>
          <span className="jmt-history__rung">{String(row.timeframe)}</span>
        </span>
      ),
    },
    {
      key: 'symbol',
      label: phone ? '' : 'Instrument',
      render: (row: DataTableRow) => (
        <span className="jmt-fill__instrument" aria-label={String(row.symbol)}>
          <CoinMark symbol={String(row.symbol)} size={phone ? 16 : 18} />
          {phone ? null : String(row.symbol)}
        </span>
      ),
    },
    {
      key: 'side',
      label: 'Side',
      // The same rule as the detail sheet: the side prints only when it disagrees with the
      // direction, because on a spot venue an entry is a buy and an exit is a sell — and when they
      // disagree the row is an F-D84 phantom.
      render: (row: DataTableRow) => {
        const closing = Boolean(row.reduce_only);
        const parts = [closing ? 'exit' : 'entry'];
        if (String(row.side) !== (closing ? 'sell' : 'buy')) parts.push(String(row.side));
        if (row.gapped) parts.push(phone ? 'gap' : 'gapped');
        return parts.join(' · ');
      },
    },
    // 🔴 **Price is NOT rounded, and that is the one column where rounding would destroy it.**
    //
    // Two decimals is right for a euro amount and wrong for a price: roughly seventy of this
    // venue's seventy-seven pairs trade below €1 and many below €0.0001, so `0.0000000262` at 2dp
    // is `0.00` — a number where there is a measurement, on the record of what was actually
    // traded. The venue's own tick size already decides how many places a price has, so the exact
    // string is both the truth and about as short as it can honestly be.
    ...(phone ? [] : [{ key: 'price', label: 'Price', align: 'right' as const, mono: true }]),
    {
      key: 'notional',
      label: phone ? '€' : 'Notional (€)',
      align: 'right',
      mono: true,
      render: notionalCell,
    },
    // ⚠ **Fee at four places here, two on the detail sheet.** This table has the width, and at this
    // fleet's sizes most fees are sub-cent — `0.0006` says something `0.00` does not. The detail
    // sheet sits in a 706px dialog and was explicitly asked for two.
    ...(phone
      ? []
      : [
          {
            key: 'fee',
            label: 'Fee (€)',
            align: 'right' as const,
            mono: true,
            render: (row: DataTableRow) => <Num value={money4(row.fee)} kind="money" />,
          },
          {
            key: 'liquidity',
            label: 'Liquidity',
            render: (row: DataTableRow) => String(row.liquidity),
          },
        ]),
  ];
}
/** One figure in the header rail. `--num-lg`, so it is clearly subordinate to the 48px hero. */
function FleetStat({
  label,
  value,
  tone,
  title,
}: {
  label: string;
  value: number | string;
  tone: string;
  // ⚠ On the `<div>` rather than the `<dd>`, so hovering the label works too — the label is the
  // part a reader who does not know what "Directional" means will point at.
  title?: string;
}): JSX.Element {
  return (
    <div className="jmt-fleet__stat" title={title}>
      <dt>{label}</dt>
      <dd className="jmt-sig" data-tone={tone}>
        {value}
      </dd>
    </div>
  );
}

/**
 * The marks, named once, near the data.
 *
 * ⚠ **This replaces two 11px paragraphs at the bottom of the screen**, which is where explanatory
 * text goes to be unread. A legend belongs beside the thing it decodes; the honesty burden the
 * paragraphs carried is not reduced by moving them, and the one sentence that is genuinely a
 * caveat rather than a key — modelled fills — keeps its own line here.
 */
function FleetLegend(): JSX.Element {
  const marks: ReadonlyArray<[Mark, string, string]> = [
    ['pass', 'pass', 'pass'],
    ['isolated', 'isolated', 'isolated'],
    ['too_early', 'too early', 'quiet'],
    ['flagged', 'flagged', 'flagged'],
    ['not_ranked', 'not ranked', 'none'],
    // D73. Listed straight after `pass` would be tidier, but adjacency to `isolated` is what makes
    // it read as the *third* qualified pass rather than as a new symbol — both enclose a check, and
    // the enclosure is the distinction.
    ['unresolved', 'promotable, execution unresolved', 'pass'],
  ];
  return (
    <div className="jmt-legend">
      <ul className="jmt-legend__keys">
        {marks.map(([mark, label, tone]) => (
          <li key={mark}>
            <SigMark mark={mark} tone={tone} />
            <span>{label}</span>
          </li>
        ))}
        {/* ⚠ Parenthesised rather than joined by a second em dash. `— not computable yet — never
            zero` puts two dashes in one seven-word key, and the first of them is the thing being
            defined: it read as a typo. */}
        <li>
          <span className="jmt-legend__dash">{NONE}</span>
          <span>not computable yet (never zero)</span>
        </li>
      </ul>
      {/* 🔴 **D73, and it belongs beside the mark rather than in a footnote.** A screen that ranks
          shorts next to longs without saying what the ranking did about direction is the *"screen
          stating something false"* shape §7 names. The rows carry `blocked_by`; these are the two
          sentences that make the *mark* and the *number* readable without hovering a row. */}
      <p className="jmt-note-text">
        <strong>Every candidate is ranked against the control that matches its direction.</strong>{' '}
        A long is measured against buy-and-hold, a short against sell-and-hold, and a market-neutral
        book against zero &mdash; so the <em>excess</em> column is what ranks, not the raw return.
        Measured 2026-08-17: <strong>58 of 74</strong> instruments were in a downtrend, so a positive
        short return on its own says almost nothing.
      </p>
      <p className="jmt-note-text">
        <strong>Short candidates are modelled, and promotable.</strong> OKX&rsquo;s perpetuals are
        USDT-margined and this account is spot-only, so a short is simulated on the same EUR spot
        bars with a funding charge and never reaches a venue. It can still lead the fleet and earn
        the G1&ndash;G12 gate &mdash; that is all <em>promotable</em> has ever meant. A{' '}
        <span className="jmt-legend__inline"><SigMark mark="unresolved" tone="pass" /></span> row has
        earned it <em>and</em> needs the venue and Box&nbsp;1 decisions taken <em>before</em> the
        gate runs.
      </p>
      <p className="jmt-note-text">
        Fills are <strong>modelled</strong>, not executed. These candidates place no orders: the
        numbers answer <em>did this idea work?</em> and never <em>can we execute it?</em> &mdash;
        there is no queue position, no real slippage and no partial fill at size. That is Demo&rsquo;s
        and Micro-live&rsquo;s question, and nothing here makes either optional.
      </p>
    </div>
  );
}

/**
 * The default view: one row per family, expanding to §8's neighbourhood reading.
 *
 * ⚠ **One family open at a time.** Seven collapsed rows is the morning read; comparing two families
 * is what the second segment's table is for. The disclosure is owned here rather than inherited,
 * because the table is — see the file docstring on why `DataTable` could not carry this view.
 */
function FamilyView({ groups, filter }: { groups: FamilyGroup[]; filter: string }): JSX.Element {
  const [expanded, setExpanded] = useState<string | undefined>(undefined);
  const shown = useMemo(
    () => groups.filter(FILTERS.find((f) => f.value === filter)?.keep ?? (() => true)),
    [groups, filter],
  );

  return (
      <table className="jmt-fam">
        <colgroup>
          <col className="jmt-fam__c-name" />
          <col className="jmt-fam__c-reading" />
          <col className="jmt-fam__c-evidence" />
          <col className="jmt-fam__c-sweep" />
          {/* 🔴 The spacer, and it is the whole reason this table is readable. A `width: 100%`
              table with no spare track divides the viewport among its real columns, which is what
              put ~230px behind a one-digit count. The remainder is parked here instead. */}
          <col />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Family</th>
            <th scope="col">Reading</th>
            <th scope="col">Evidence</th>
            <th scope="col">Sweep</th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 ? (
            <tr>
              <td className="jmt-fam__empty" colSpan={5}>
                No family matches that filter. The counts on each segment say how many would.
              </td>
            </tr>
          ) : null}
          {shown.map((group) => {
            const reading = readingOfFamily(group);
            const open = expanded === group.family;
            // ⚠ A family with no swept dimension has nothing to disclose, and used to be clickable
            // anyway — nine of ten rows in the fixture. A dead affordance is worse than none: it
            // teaches the reader that expanding does nothing.
            const expandable = group.dimensions.length > 0;
            return (
              <Fragment key={group.family}>
                <tr
                  className="jmt-fam__row"
                  data-open={open ? '' : undefined}
                  data-expandable={expandable ? '' : undefined}
                >
                  <th scope="row">
                    {expandable ? (
                      <button
                        type="button"
                        className="jmt-fam__toggle"
                        aria-expanded={open}
                        onClick={() => setExpanded(open ? undefined : group.family)}
                      >
                        <span aria-hidden="true" className="jmt-fam__chev">
                          <svg
                            viewBox="0 0 24 24"
                            width="14"
                            height="14"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M9 6l6 6-6 6" />
                          </svg>
                        </span>
                        <span className="jmt-fam__name">{group.family}</span>
                      </button>
                    ) : (
                      <span className="jmt-fam__name jmt-fam__name--flat">{group.family}</span>
                    )}
                  </th>
                  <td data-label="Reading">
                    <span className="jmt-verdict" data-tone={reading.tone}>
                      {reading.label}
                      {group.neighbourhoods > 1 ? ` ×${group.neighbourhoods}` : ''}
                    </span>
                  </td>
                  <td data-label="Evidence">
                    <span className="jmt-count-set">
                      <Count mark="pass" tone="pass" value={group.supported} label="supported passes" />
                      <Count
                        mark="isolated"
                        tone="isolated"
                        value={group.isolated}
                        label="isolated passes — non-results under §8"
                      />
                      <Count mark="flagged" tone="flagged" value={group.flagged} label="flagged" />
                    </span>
                  </td>
                  <td className="jmt-fam__sweep" data-label="Sweep">
                    {expandable ? (
                      <>
                        {group.candidates} cand{group.candidates === 1 ? '' : 's'} ·{' '}
                        {group.dimensions.length} dim{group.dimensions.length === 1 ? '' : 's'} ·{' '}
                        {group.timeframes.join(' ')}
                      </>
                    ) : (
                      <span
                        className="jmt-fam__unswept"
                        title="Not a point on any declared dimension, so §8 cannot read it as a neighbourhood"
                      >
                        {group.candidates} cand{group.candidates === 1 ? '' : 's'} · not a sweep
                      </span>
                    )}
                  </td>
                  <td />
                </tr>
                {open && expandable ? (
                  <tr className="jmt-fam__drawer">
                    <td colSpan={5}>
                      <FamilySweeps group={group} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
  );
}

/**
 * A count with its own mark as its label.
 *
 * 🔴 **Zero renders as `·`, and this is the single change that made the family list scannable.**
 * Nine of ten rows in the fixture read `1 0 0 0 0 0` — six columns of a digit that means *nothing
 * to count*, competing for attention with the one digit that means something. The mark dims with
 * it, so a silent count is silent in both channels rather than just one.
 *
 * ⚠ This is not the em-dash rule and must not be confused with it. `—` means *not computable yet*;
 * these counts are always computable, and `·` means *computed, and it is none*.
 */
function Count({
  mark,
  tone,
  value,
  label,
}: {
  mark: Mark;
  tone: string;
  value: number;
  label: string;
}): JSX.Element {
  const silent = value === 0;
  return (
    <span className="jmt-count" data-silent={silent ? '' : undefined} title={`${value} ${label}`}>
      <SigMark mark={mark} tone={silent ? 'silent' : tone} />
      <span className="jmt-count__n">{silent ? '·' : value}</span>
    </span>
  );
}

/**
 * One family's expansion: a dimension per row, the timeframes side by side.
 *
 * The grid is `max-content` for the dimension name and one track per timeframe, so a dimension's
 * two runs sit on one line and *"a neighbourhood holding on both is the stronger evidence"* is one
 * glance rather than two.
 */
function FamilySweeps({ group }: { group: FamilyGroup }): JSX.Element {
  return (
    <div className="jmt-sweeps">
      {/* 🔴 **The column count travels as a custom property, not as `gridTemplateColumns`.**
          An inline `grid-template-columns` beats every media query, so the desktop's
          `dimension | 1h | 4h` grid survived onto a 430px phone and overflowed its own card — the
          same class of defect as the vendored chrome being inline-styled, in this screen's own
          code. A custom property carries the one thing React knows (how many rungs) and leaves the
          *layout* to CSS, where a breakpoint can reach it. */}
      {group.dimensions.length === 0 ? null : (
        <div
          className="jmt-sweeps__grid"
          style={{ '--jmt-tf-count': group.timeframes.length } as React.CSSProperties}
        >
          <span />
          {group.timeframes.map((timeframe) => (
            <span className="jmt-sweeps__head" key={timeframe}>
              {timeframe}
            </span>
          ))}
          {group.dimensions.map((dimension) => {
            const runs = runsFor(group, dimension);
            // 🔴 **A rung with no sweep is dropped, not labelled.** It used to render a cell
            // saying `not swept on this rung` — and before that a bare em dash, which read as a
            // typo. Both were the same mistake: spending a whole cell, in the densest block on
            // the screen, on the *absence* of one.
            //
            // ⚠ **The fact is kept, at the width it is worth.** §8 repeats each sweep on both
            // rungs, so a dimension that appears on only one is a seeding question rather than a
            // nothing — and it is now one word beside the dimension's own name, where it is read
            // once instead of once per empty cell.
            const present = runs.filter((run): run is DimensionRun => run !== null);
            const only =
              present.length < group.timeframes.length
                ? present.map((run) => run.timeframe).join(' ')
                : null;
            return (
              <Fragment key={dimension}>
                <span className="jmt-sweeps__dim">
                  {dimension}
                  {only === null ? null : (
                    <span
                      className="jmt-sweeps__only"
                      title={`§8 repeats each sweep on both rungs; this one exists on ${only} alone`}
                    >
                      {only} only
                    </span>
                  )}
                </span>
                {/* 🔴 **Each run is pinned to its own rung's column.** Dropping the empty cells
                    means a 4h-only dimension would otherwise flow into the *first* run column and
                    sit under the `1H` header — a sweep filed under the wrong timeframe, which is
                    worse than the empty cell it replaced. The index is +2 because column 1 is the
                    dimension name. Carried as a custom property so the phone's single-column
                    layout can ignore it. */}
                {present.map((run) => (
                  <Run
                    run={run}
                    column={group.timeframes.indexOf(run.timeframe) + 2}
                    key={`${run.dimension}-${run.timeframe}`}
                  />
                ))}
              </Fragment>
            );
          })}
        </div>
      )}
      {group.unswept.length === 0 ? null : (
        <p className="jmt-note-text">
          <strong>
            {group.unswept.length} candidate{group.unswept.length === 1 ? '' : 's'} not on a swept
            dimension
          </strong>{' '}
          &mdash; {group.unswept.map((row) => `#${row.deployment_id} ${row.timeframe}`).join(', ')}.
          The <code>buy_and_hold</code> control is one of these by design. Anything else is a
          candidate §3 cannot read as a neighbourhood, and therefore treats as a non-result: it is
          shown rather than dropped because a silently omitted row is a seeding mistake nobody finds.
        </p>
      )}
    </div>
  );
}

/**
 * 🔴 **The run, and the highest-value element on the screen.**
 *
 * *"`⧗ ✓ ✓ ⧗` is legible as a neighbourhood and `⧗ ✓ ⧗ ⧗` as an isolated point. This is the whole
 * §8 test rendered as four glyphs."* Four things about the rendering serve that one comparison:
 *
 * 1. **The points are in parameter order**, in equal-width cells, so the marks form a row that can
 *    be taken in at once rather than read left to right.
 * 2. **The parameter value sits beneath its own mark**, so the run reads as a shape or as a sweep
 *    without a second lookup.
 * 3. 🔴 **The longest passing run is bracketed.** `longestPassSpan` gives the position and
 *    `readingOf` gives the verdict from that same span, so the drawing and the label cannot
 *    disagree — and the reader no longer has to count adjacent marks to apply §8's test. This is
 *    the one thing the previous rendering left entirely to the eye.
 * 4. **A pass with no passing neighbour is `(✓)`, drained.** §8 requires it — *"an isolated pass is
 *    marked as a non-result, not as a winner"* — and the API cannot make the distinction, because
 *    significance is per candidate and this one is a property of its neighbours.
 */
function Run({ run, column }: { run: DimensionRun; column: number }): JSX.Element {
  const span = longestPassSpan(run.points.map((point) => point.candidate.significance));
  const reading = READINGS[run.reading] ?? NO_PASS;
  // 🔴 **A run where nothing is computable draws no metrics row at all.** Every point in a young
  // family returns `null` for `net_pct`, so the row rendered as four em dashes under four
  // identical rings — a line of punctuation that says exactly what the marks above it already
  // said. ⚠ Any single point having a value keeps the row for the whole run, because a gap next
  // to a figure is information: it means *that* point has no measurement, which is not the same
  // statement as *this family has none yet*.
  const anyMetric = run.points.some((point) => point.candidate.net_pct !== null);
  return (
    <span
      className="jmt-run"
      data-reading={run.reading}
      style={{ '--jmt-col': column } as React.CSSProperties}
    >
      {/* ⚠ **The rung, repeated on every run, and hidden on desktop.** The grid's column headers
          name the timeframes once at the top, which works while the runs sit in columns under
          them. On a phone the grid collapses to one column and those headers stop pointing at
          anything, so each run has to say which rung it is. Rendered rather than duplicated in
          CSS because a `::before` cannot read a prop. */}
      <span className="jmt-run__tf">{run.timeframe}</span>
      <span
        className="jmt-run__grid"
        style={{ gridTemplateColumns: `repeat(${run.points.length}, var(--jmt-run-cell))` }}
      >
        {run.points.map((point) => (
          <Point
            point={point}
            dimension={run.dimension}
            showMetrics={anyMetric}
            key={point.candidate.deployment_id}
          />
        ))}
        {span.length > 0 ? (
          <span
            aria-hidden="true"
            className="jmt-run__bracket"
            style={{ gridColumn: `${span.start + 1} / span ${span.length}` }}
          />
        ) : (
          <span aria-hidden="true" className="jmt-run__rule" />
        )}
      </span>
      <span className="jmt-run__reading">
        <strong>{reading.label}</strong>
        {reading.note === '' ? null : <span> &mdash; {reading.note}</span>}
      </span>
    </span>
  );
}

function Point({
  point,
  dimension,
  showMetrics,
}: {
  point: SweepPoint;
  dimension: string;
  showMetrics: boolean;
}): JSX.Element {
  const row = point.candidate;
  const supported = point.supported;
  const isolatedPass = row.significance === PASS && !supported;
  // The title is the whole candidate in one hover: what it is, what the rule made of it, and why.
  // `blocked_by` is the progress text for `too early` and the reason for `flagged`, and the
  // isolation note is added here because no field carries it — it is a fact about the run.
  const title = [
    `${dimension} = ${String(point.value)}${point.isDefault ? ' (family default)' : ''}`,
    `#${row.deployment_id} v${row.version} · ${row.trades} closed trades` +
      `${row.open_positions > 0 ? `, ${row.open_positions} open` : ''} · ${row.days} days`,
    isolatedPass
      ? 'promotable, but no adjacent point passes — §8 reads this as a non-result'
      : row.significance,
    row.blocked_by ?? '',
  ]
    .filter((line) => line !== '')
    .join('\n');

  return (
    <span className="jmt-run__point" title={title}>
      <SigMark
        mark={markFor(row.significance, supported, row.execution_unresolved)}
        tone={toneOf(row.significance, supported)}
        title={title}
      />
      <span className="jmt-run__value">
        {String(point.value)}
        {point.isDefault ? (
          <span className="jmt-run__default" title="The family default">
            •
          </span>
        ) : null}
      </span>
      {/* 🔴 **The point's own numbers, in the cell rather than behind a hover.**
          Until now a run showed a mark and a parameter value, and everything else about the
          candidate — what it returned, how many trades it has — lived in a `title`. A native
          tooltip is a half-second delay, one candidate at a time, and it cannot be compared
          against its neighbours, which is the entire operation this run exists to support.

          ⚠ **Two figures, not five.** The question a run answers is *does the result hold across
          adjacent settings*, and that is answered by the return and by whether there is enough
          evidence behind it. Sharpe, drawdown and divergence are per-candidate questions and they
          have a whole table of their own on the other segment. Five figures per point would make
          the marks — the thing the eye is supposed to sweep — the smallest part of the run again.

          `net_pct` keeps its profit/loss colour here because it is a return; the trade count is
          quiet, because it qualifies the return rather than competing with it. */}
      {showMetrics ? (
      <span className="jmt-run__metrics">
        <Num
          value={row.net_pct === null ? null : Number(row.net_pct)}
          kind="percent"
          dp={1}
          step="xs"
        />
        <span
          className="jmt-run__trades"
          title={`${row.trades} closed round trips over ${row.days} days; ${row.open_positions} still open`}
        >
          {row.trades}t{row.open_positions > 0 ? ` +${row.open_positions}` : ''}
        </span>
      </span>
      ) : null}
    </span>
  );
}

/**
 * The flat table: every candidate, all nine columns, and its own sorting.
 *
 * ⚠ **It keeps its column sorting while the family view has none**, and that is not an
 * inconsistency: a flat list has no adjacency for a sort to destroy, and the columns here are
 * per-candidate measurements whose whole use is being ranked. §8's prohibition is on sorting the
 * *sweep*.
 *
 * ## 🔴 Why this is app-owned rather than a `DataTable`
 *
 * Same reason the family view is, and the family view proved the point. `DataTable` is
 * `width: 100%` on the browser's automatic layout with no per-column width, so nine columns get
 * the viewport divided among them by content — which is what put ~1000px between the numbers
 * describing one candidate. Capping the container helped and did not fix it: the *proportions*
 * were still decided by whichever candidate happened to have the longest name.
 *
 * What being app-owned buys, and each of these is something the operator named:
 *
 * - **Fixed tracks**, so a column is the width its content needs and not the width its longest
 *   row negotiated. The identity columns are wide, the measurements are narrow and equal, and a
 *   row reads as a row.
 * - **A real hover.** `DataTable` has none. Twenty rows of nine near-identical figures is exactly
 *   the table where the eye loses its line, and a row highlight is the cheapest fix there is.
 * - **A group boundary** between what a candidate *is* and what it *did*. One hairline, at the one
 *   place the reading changes direction.
 * - **A sort affordance that is visible before it is used.** The vendored header hides its glyph
 *   at `opacity: 0` until the column is active, so the table did not look sortable.
 * - **Room for the sparkline.** It was 64×24 in a dense row; a 30-day shape at that size is a
 *   texture rather than a curve.
 */
/**
 * §2's three regimes, in `jmt_research.regime.REGIMES` order, with the track letter and the word
 * the tooltip uses.
 *
 * ⚠ **The keys are the classifier's own vocabulary**, not the design sketch's `up` / `down` /
 * `range`. One concept, one set of names — the rule `EXIT_REASON_LABELS` states for the other
 * vocabulary this screen shares with the server.
 */
const REGIME_TRACKS: ReadonlyArray<readonly [key: string, letter: string, name: string]> = [
  ['uptrend', 'U', 'Up-trend'],
  ['downtrend', 'D', 'Down-trend'],
  ['range', 'R', 'Range-bound'],
];

/**
 * §2's first and third minimums, which the merged Evidence cell now quotes.
 *
 * ⚠ **These are client constants and `regime_minimum` is not, and the asymmetry is deliberate
 * rather than an oversight.** G10's threshold arrived with task 4.1 and is the one §2 minimum that
 * has moved, so it travels on the payload — a client constant is how a screen ends up asserting a
 * threshold the engine has stopped using. These two have been fixed since the pre-registration and
 * are frozen for the running cohort by the same rule that froze the rest of §2. If either ever
 * moves, it moves onto the payload beside `regime_minimum` rather than being edited here.
 */
const MIN_TRADES = 100;
const MIN_INSTRUMENTS = 10;

/**
 * §2's fourth minimum in words, for the cell's own tooltip.
 *
 * ⚠ **Written once and used by both the header and every cell.** Three one-letter track labels are
 * legible only to someone who already knows §2 has a fourth minimum — which is the thing this
 * column exists to tell them. The letters stay because the column is 84px; the words arrive on
 * hover.
 */
function regimeTitle(counts: Record<string, number> | null, minimum: number): string {
  if (counts === null) {
    return (
      'G10 regime coverage (§2) has not been evaluated — there are no closed trades to classify.' +
      '\nThis is not the same as zero coverage: nothing has been measured yet.'
    );
  }
  const named = REGIME_TRACKS.map(([key, , name]) => [name, counts[key] ?? 0] as const);
  const shortOf = named.filter(([, value]) => value < minimum);
  return [
    `G10 regime coverage — completed trades in each market regime, against the ${minimum} the` +
      ' rule requires in EVERY one of the three.',
    ...named.map(
      ([name, value]) =>
        `${name}: ${value} of ${minimum}${value >= minimum ? ' ✓' : ` — ${minimum - value} short`}`,
    ),
    shortOf.length > 0
      ? `\nNot yet covered: ${shortOf.map(([name]) => name.toLowerCase()).join(', ')}. A candidate` +
        " can clear trades, bars and instruments and still fail here — which is why §2's fourth" +
        ' minimum needed a column.'
      : '\nAll three covered.',
  ].join('\n');
}

/**
 * How close a leader's margin has to be before it is not a lead at all.
 *
 * ⚠ **A margin under this reads `within noise` rather than a number**, because §3 promotes exactly
 * one candidate and `+0.04 pt` printed beside `#1` invites reading a tie as a decision.
 */
const RANK_NOISE = 0.35;

/**
 * The rungs in interval order, for the cohort line.
 *
 * ⚠ Not derived from `Timeframe` in the generated client: that enum's declaration order is the
 * schema's, which is alphabetical, and `15m` before `1h` before `4h` is what a reader expects.
 */
const RUNG_ORDER: ReadonlyArray<string> = ['1m', '5m', '15m', '1h', '4h', '1d'];

/** The hint the `Regime` heading carries, named once so the header and the tooltip agree. */
const MIN_TRADES_HINT =
  'completed trades in each of up-trend, down-trend and range-bound. §2 requires its minimum in' +
  ' ALL THREE. Hover a row for its counts.';

/**
 * Rank among promotable candidates on `excess_pct`, with the leader carrying its margin.
 *
 * 🔴 **Computed over the whole cohort, once, rather than per row.** Rank is a property of the set,
 * and a per-row derivation would be N passes over the same list agreeing by luck.
 *
 * ⚠ **Only candidates that are BOTH promotable and measured are ranked.** A candidate with no
 * excess has not earned a position in a ranking of excess — the same rule `numeric` applies when it
 * sorts `null` last in both directions.
 */
function rankOf(items: FleetCandidate[]): Map<number, { rank: string; note: string }> {
  const ranked = items
    .filter((row) => row.significance === PASS && row.excess_pct !== null)
    .sort((a, b) => Number(b.excess_pct) - Number(a.excess_pct));
  const out = new Map<number, { rank: string; note: string }>();
  ranked.forEach((row, index) => {
    let note = '';
    if (index === 0 && ranked.length > 1) {
      const margin = Number(row.excess_pct) - Number(ranked[1]!.excess_pct);
      note = margin < RANK_NOISE ? 'within noise' : `+${margin.toFixed(2)} pt`;
    }
    out.set(row.deployment_id, { rank: `#${index + 1}`, note });
  });
  return out;
}

/**
 * The five columns that are never dropped, and their track widths — the same set the phone renders.
 *
 * 🔴 **Identity, what it holds, what it returned, the verdict, the trend.** Everything else is
 * detail that the sheet carries too; these five are the row's reason to exist, and the phone layout
 * already proved they are the irreducible set.
 */
const BASE_COLUMNS = 152 + 142 + 66 + 116 + 80;

/**
 * The optional columns in the order they EARN their space back, widest budget last.
 *
 * 🔴 **The order is a value judgement and it is the whole of this design, so it is written down
 * rather than implied by a media query.** Reading up from the bottom, this is also the drop order:
 *
 * - `rank` goes first, because it is derived from `excess` — the same argument `RowView` makes for
 *   why it should not be a column at all.
 * - `bt` then `risk`: both are withheld below §2's 28-day window anyway (D68), so on a young cohort
 *   they are em dashes taking 138px between them.
 * - `age` and `regime` are real measurements, but neither decides a promotion.
 * - `evidence` is the last to go, because *"is there enough data yet"* outranks everything except
 *   the answer itself.
 * - `excess` is never dropped above the base set: it is the number §3 decides on (D73).
 *
 * ⚠ **Widths must match `app.css`'s `.jmt-rows__c-*` tracks.** They are duplicated here because the
 * fit decision needs arithmetic and CSS cannot do it; `fleetColumnFit.test.ts` pins the total at
 * 1198 so the two cannot drift silently.
 */
const OPTIONAL_COLUMNS: ReadonlyArray<readonly [key: string, width: number]> = [
  ['excess', 78],
  ['evidence', 162],
  ['regime', 84],
  ['age', 90],
  ['risk', 76],
  ['bt', 62],
  ['rank', 90],
];

/** Which optional columns fit in `available` px, and what the resulting table width is. */
export function fitColumns(available: number): { on: ReadonlySet<string>; minWidth: number } {
  const on = new Set<string>();
  let total = BASE_COLUMNS;
  for (const [key, width] of OPTIONAL_COLUMNS) {
    // 🔴 **Stop at the first column that does not fit — do not skip it and try the next.** Skipping
    // would render a wider column later in the list while a narrower, more important one was
    // dropped, so the set would no longer follow the priority order above.
    if (total + width > available) break;
    total += width;
    on.add(key);
  }
  return { on, minWidth: total };
}

/**
 * The flat table: every candidate, up to twelve columns, and its own sorting.
 *
 * ## 🔴 S14 task 2 — 1388px of columns became 1198px, and the table gained two columns doing it
 *
 * Measured at a 1280px viewport, the old twelve columns summed to **1388px inside a 1206px
 * scroller**, so 182px of the table — most of `Significance` and all of `30 days` — sat behind a
 * horizontal scroll on the widest layout this screen has. The width came back from four places, and
 * none of them is a font size:
 *
 * - **`State` (115px → 0)** — one of four words in a column where every visible row reads
 *   `RUNNING`. Now a mark on the candidate cell; see `StateMark` for why `RUNNING` draws nothing.
 * - **`Last in / out` (196px → 78px)** — an absolute clock answering *"at what time"*, a question
 *   nobody asks of a six-week cohort. Now a relative age, with the instant kept in `title`.
 * - **`Sharpe` + `Drawdown` (202px → 84px)** — two columns of one number each, stacked into one
 *   risk cell. They are read together or not at all.
 * - **`instruments` and `book`** — properties of the *cohort*, identical on every row, so they are
 *   stated once in the panel header instead of 293 times in the table.
 *
 * What the recovered width bought: `Evidence` widened to hold four thresholds instead of one
 * (task 4.6), and two genuinely new columns — `Regime` (task 4.1, §2's fourth minimum, which the
 * screen could previously only mention in a tooltip) and `Rank` (task 4.2).
 *
 * ⚠ **`Rank` is shipped as specified and I would still argue with it.** Rank is derived from
 * `Excess`, so it belongs to the cohort rather than to a row, and a per-row `#4` invites reading
 * fourth place as a result when §3 promotes one candidate. The margin is the real datum. It is here
 * because the task asked for it and because the leader's margin has nowhere else to live yet; if
 * the hero line ever carries that margin, this column should go back to being rank alone.
 *
 * ## The phone layout is unchanged, and that is load-bearing
 *
 * Five columns — `Candidate | Holding | Net | Sig | 30d` — exactly as before. Task 4.9 gives it
 * something it did not have: `State` was already dropped below 480px, so a halted candidate looked
 * identical to a running one on a phone. The mark rides on the candidate cell, which the phone
 * keeps.
 */
function RowView({ items }: { items: FleetCandidate[] }): JSX.Element {
  const [sortKey, setSortKey] = useState('significance');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [open, setOpen] = useState<number | null>(null);
  // 🔴 **The column set is rendered, not hidden.** See `useIsPhone` for the three CSS attempts
  // this replaces; the short version is that a `<col>` survives `display: none` on every cell in
  // its column, and a fixed layout then draws the visible cells on top of the collapsed tracks.
  const phone = useIsPhone();

  // 🔴 **The column set is chosen from the MEASURED container, not from the viewport.** See
  // `useElementWidth` for the table that made this necessary: at 1280 the sidebar expands and the
  // table's room drops 192px, so the 12-column layout overflowed by 264px at 1280 while overflowing
  // by only 73px at 1279. A `min-width` ladder has to remove columns as the window grows to survive
  // that, which no media query can say.
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const available = useElementWidth(scroller);
  // ⚠ `available === 0` is *"not measured yet"*, not *"no room"* — on the desktop assume the full set
  // so the first paint is the common case rather than the narrowest one, and let the observer
  // correct it. On a phone the base five are all there ever are, so 0 is the right answer already.
  const fit = useMemo(() => fitColumns(available > 0 ? available : phone ? 0 : 1198), [available, phone]);
  const show = fit.on;

  // Task 4.2. Over `items` rather than over the sorted or filtered view: a rank that changed when
  // the operator clicked a column heading would be a different claim under the same number.
  const ranks = useMemo(() => rankOf(items), [items]);

  const rows = useMemo(() => {
    // 🔴 **Sorted by significance FIRST, and the order is deliberate.** `flagged` above
    // `promotable`: a candidate diverging from its own backtest is a bug to investigate, and a bug
    // outranks a result. The spec pins live rows above the sort on S2 for the same reason —
    // *"the question 'what is running with real money' must never depend on which column was last
    // clicked"* — and here the equivalent question is "what is broken".
    const rank: Record<string, number> = { flagged: 0, promotable: 1, too_early: 2, not_ranked: 3 };
    return [...items].sort((a, b) => {
      let order: number;
      if (sortKey === 'significance') {
        order = (rank[a.significance] ?? 9) - (rank[b.significance] ?? 9);
      } else if (sortKey === 'candidate') {
        order = a.candidate.localeCompare(b.candidate);
      } else {
        // Numeric columns. `null` sorts last in both directions: a candidate with no measurement
        // has not earned a position in a ranking of measurements.
        const left = numeric(a, sortKey);
        const right = numeric(b, sortKey);
        if (left === null && right === null) order = 0;
        else if (left === null) return 1;
        else if (right === null) return -1;
        else order = left - right;
      }
      return sortDir === 'asc' ? order : -order;
    });
  }, [items, sortDir, sortKey]);

  const onSort = (key: string): void => {
    if (key === sortKey) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <div className="jmt-rows-scroll" ref={setScroller}>
      <CohortHeader items={items} />
      {/* ⚠ **`min-width` is inline because it is arithmetic**: it is the sum of the tracks actually
          rendered, so the fixed layout has exactly the room its columns need and no more. A constant
          in `app.css` was what forced 1198px at every width. Omitted on a phone, where the media
          query supplies its own narrower tracks. */}
      <table className="jmt-rows" style={phone ? undefined : { minWidth: `${fit.minWidth}px` }}>
        {/* ⚠ **One class per column, not one shared numeric class**, because a `<col>` keeps its
            width even when every cell in that column is `display: none`. The phone hides seven
            columns and the table still measured 644px in a 430px viewport — exactly the widths of
            the `<col>`s nobody could see. A column can only be removed from a fixed layout by
            zeroing its own track.

            ⚠ **Every one of these is a BORDER-box width**, set in `app.css`. A content-box cell
            given `width: 66px` with `padding: 0 12px` holds 42px of content, so the table's total
            honours the budget while the cell hides its own value — *"an `overflow-x: auto` table
            does not overflow, it hides the answer"* in miniature. */}
        <colgroup>
          <col className="jmt-rows__c-name" />
          {show.has('evidence') ? <col className="jmt-rows__c-evi" /> : null}
          {show.has('regime') ? <col className="jmt-rows__c-regime" /> : null}
          <col className="jmt-rows__c-held" />
          {show.has('age') ? <col className="jmt-rows__c-age" /> : null}
          <col className="jmt-rows__c-net" />
          {show.has('excess') ? <col className="jmt-rows__c-excess" /> : null}
          {show.has('rank') ? <col className="jmt-rows__c-rank" /> : null}
          {show.has('risk') ? <col className="jmt-rows__c-risk" /> : null}
          {show.has('bt') ? <col className="jmt-rows__c-bt" /> : null}
          <col className="jmt-rows__c-sig" />
          <col className="jmt-rows__c-spark" />
        </colgroup>
        <thead>
          <tr>
            {/* ⚠ **`Name` on a phone, `Candidate` on the desktop**, the same abbreviation this header
                row already applies to `Sig` and `30d`. `Candidate` plus its sort arrow needs 92px and
                the phone's first track is 88 — and that track cannot grow, because the four fixed
                ones beside it sum to 238 of the 326 the table gets at 390px. Letting the arrow wrap
                was the previous answer and it is what made the desktop header ragged. */}
            <Th col="candidate" label={phone ? 'Name' : 'Candidate'} {...{ sortKey, sortDir, onSort }} />
            {/* Sorted on `trades`, which is the cell's lead value and §2's first minimum. The bars
                beneath it are the more honest measure of evidence but not the one an operator sorts
                this column to rank on. */}
            {show.has('evidence') ? (
              <Th col="trades" label="Evidence" {...{ sortKey, sortDir, onSort }} />
            ) : null}
            {/* Not sortable, and a header without the affordance says so: three tracks against one
                threshold have no single order, and sorting on any one of them would rank the fleet
                by the regime that happened to be picked. */}
            {show.has('regime') ? (
              <th scope="col" title={`G10 regime coverage — ${MIN_TRADES_HINT}`}>
                {/* ⚠ **`Regime`, not `Regime (U/D/R)`.** The parenthetical asked for 124px in an 84px
                    track, and letting it wrap put this heading 45px above its neighbours. The U/D/R
                    letters are IN every cell, which is where a legend for three tracks belongs, and
                    the full rule is on this heading's own `title`. */}
                Regime
              </th>
            ) : null}
            {/* Not sortable: a set of coins has no order, and the useful comparison — how many —
                is already the `+N open` in Evidence beside it.

                🔴 **Shown on the phone too**, unlike the columns around it. Three 14px marks cost
                less width than any number in the table, and "what is this candidate actually in" is
                the question an operator opens their phone to ask. */}
            <th scope="col">Holding</th>
            {show.has('age') ? (
              <Th
                col="last_entry_at"
                // `Age`, not `Age in / out`: the cell renders `4h / never`, so the slash is already
                // on screen and the heading was spending 20px restating it on a second line.
                label="Age"
                align
                title="Age of the last entry and the last exit. Hover a cell for the instant."
                {...{ sortKey, sortDir, onSort }}
              />
            ) : null}
            <Th col="net_pct" label="Net" align group={!phone} {...{ sortKey, sortDir, onSort }} />
            {/* 🔴 **The column §3's promotion is actually decided on — D73.** Immediately right of
                `Net` so the pair reads together: a healthy-looking return beside the bar it did not
                clear is the whole point.

                ⚠ **It now carries its own control (task 4.5)**, which removes the hover from the
                one column the promotion is decided on. `−1.4%` is unreadable without knowing what
                it is short of, and the bar differs per row by direction.

                ⚠ **Headed `Excess`, not `Excess vs control`.** That label asked for 172px in a 78px
                track and was the worst offender in the ragged header row the operator reported. The
                control is named on the cell's own sub-line (`vs 1.20%`) and in its `title`, which is
                strictly better than a heading anyway — it differs per row by direction. */}
            {show.has('excess') ? (
              <Th col="excess_pct" label="Excess" align {...{ sortKey, sortDir, onSort }} />
            ) : null}
            {/* Not sortable, deliberately: this column IS the order of `Excess`, so a sort control
                here would be a second way to do one thing — and one of the two would have to lose
                the tie-break rule the other applies. */}
            {show.has('rank') ? (
              <th
                scope="col"
                className="jmt-rows__th-n"
                title="Rank among promotable candidates on Excess. The leader carries its margin over the runner-up."
              >
                Rank
              </th>
            ) : null}
            {show.has('risk') ? (
              <Th
                col="sharpe"
                label="Risk"
                align
                title="Sharpe over max drawdown. Both are withheld below §2's 28-day window (D68)."
                {...{ sortKey, sortDir, onSort }}
              />
            ) : null}
            {show.has('bt') ? (
              <Th col="divergence_sigma" label="vs BT" align {...{ sortKey, sortDir, onSort }} />
            ) : null}
            <Th col="significance" label={phone ? 'Sig' : 'Significance'} {...{ sortKey, sortDir, onSort }} />
            {/* Not sortable: a shape has no order. */}
            <th scope="col">{phone ? '30d' : '30 days'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            // A halted or stopped candidate is still worth reading — *"the archive of failures is
            // valuable and should not be visually noisy"* — but it is not accumulating evidence,
            // and the row should say so without a legend.
            const running = row.state === 'running' || row.state === 'draining';
            const rank = ranks.get(row.deployment_id);
            return (
              <tr
                key={row.deployment_id}
                data-quiet={running ? undefined : ''}
                // ⚠ **A row is a button, and the whole row is the target.** A dedicated "details"
                // column would be a thirteenth column on a table that is already the widest thing
                // on the screen, and a link on the name alone is a 120px target inside a 52px row.
                // `tabIndex` and the key handler are what stop this being a mouse-only affordance.
                className="jmt-rows__row"
                tabIndex={0}
                role="button"
                aria-label={`Details for ${row.candidate} v${row.version}`}
                onClick={() => setOpen(row.deployment_id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setOpen(row.deployment_id);
                  }
                }}
              >
                {/* 🔴 **The state mark rides HERE, on the identity, and that is task 4.9.** The
                    exception belongs beside the name it qualifies rather than in a column of its
                    own, and this cell survives the phone layout — so a halted candidate is now
                    distinguishable from a running one at 390px, which it was not when `State` was
                    simply dropped below 480px. */}
                <th scope="row">
                  <span className="jmt-rows__ident">
                    <StateMark state={row.state} />
                    <span className="jmt-rows__name">{row.candidate}</span>
                  </span>
                  <span className="jmt-rows__meta">
                    v{row.version} · {row.timeframe}
                    {row.direction === 'long' ? '' : ` · ${row.direction}`}
                  </span>
                </th>
                {show.has('evidence') ? (
                  <td>
                    <EvidenceCell row={row} />
                  </td>
                ) : null}
                {show.has('regime') ? (
                  <td>
                    <RegimeCell row={row} />
                  </td>
                ) : null}
                <td className="jmt-rows__held">
                  <HeldCoins held={row.held} heldShort={row.held_short} phone={phone} />
                </td>
                {show.has('age') ? (
                  <td className="jmt-rows__n">
                    <ActivityAge entry={row.last_entry_at} exit={row.last_exit_at} />
                  </td>
                ) : null}
                {/* `kind="percent"` gives `+2.4%` with profit/loss colour. ⚠ Colour is kept HERE
                    and on `Excess` only, following `Num`'s own guidance — its `colour` prop is
                    documented as *"set false to suppress profit/loss colour (e.g. inside a dense
                    fleet table)"*. Two coloured columns, not five. */}
                <td className={phone ? 'jmt-rows__n' : 'jmt-rows__n jmt-rows__n--group'}>
                  <Num
                    value={row.net_pct === null ? null : Number(row.net_pct)}
                    kind="percent"
                    dp={1}
                    step="sm"
                  />
                </td>
                {/* 🔴 **Coloured, and it is the ONE place the comment above gets amended.** After
                    D73 the value that decides the promotion is this one, and a green `Net` beside an
                    uncoloured negative `Excess` is precisely the misreading the column was added to
                    prevent. */}
                {/* ⚠ **The cell keeps `excessTitle` even though task 4.5 put the control IN the
                    cell.** The sub-line says *what* the bar is; the hover says *which control it
                    is* — buy-and-hold, sell-and-hold, or a flat book — and that differs per row by
                    direction. Task 4.5 removed the screen's DEPENDENCE on the hover, which is not
                    the same as removing the hover. */}
                {show.has('excess') ? (
                  <td className="jmt-rows__n" title={excessTitle(row)}>
                    <span className="jmt-rows__stack">
                      <Num
                        value={row.excess_pct === null ? null : Number(row.excess_pct)}
                        kind="percent"
                        dp={1}
                        step="sm"
                      />
                      {/* ⚠ **2dp and no leading plus**, matching `excessTitle`'s own formatting:
                          this is the bar being quoted, not a signed P&L. */}
                      <span className="jmt-rows__sub">
                        {row.control_pct === null
                          ? 'no control yet'
                          : `vs ${Number(row.control_pct).toFixed(2)}%`}
                      </span>
                    </span>
                  </td>
                ) : null}
                {show.has('rank') ? (
                  <td className="jmt-rows__n">
                    {rank === undefined ? (
                      <span className="jmt-rows__none">{NONE}</span>
                    ) : (
                      <span className="jmt-rows__stack">
                        <span className="jmt-rows__rank">{rank.rank}</span>
                        {rank.note ? <span className="jmt-rows__sub">{rank.note}</span> : null}
                      </span>
                    )}
                  </td>
                ) : null}
                {/* Task 4.6's other merge: two columns of one number each become one cell. The
                    drawdown is rendered negative because a drawdown is a fall and the API stores it
                    unsigned — the sign belongs to the presentation. */}
                {show.has('risk') ? (
                  <td className="jmt-rows__n">
                    <span className="jmt-rows__stack">
                      <Num
                        value={row.sharpe === null ? null : Number(row.sharpe)}
                        kind="ratio"
                        dp={2}
                        step="sm"
                      />
                      <span className="jmt-rows__sub">
                        {row.max_drawdown_pct === null
                          ? NONE
                          : `−${Number(row.max_drawdown_pct).toFixed(1)}%`}
                      </span>
                    </span>
                  </td>
                ) : null}
                {/* ⚠ `Num` has no σ unit and should not gain one: it formats numbers on the
                    product's ramp, and a bespoke unit per caller is how a shared formatter becomes
                    a switch statement. The glyph is appended here, and only when there is a value —
                    `—σ` would be worse than `—`. */}
                {show.has('bt') ? (
                  <td className="jmt-rows__n">
                    {row.divergence_sigma === null ? (
                      <span className="jmt-rows__none">{NONE}</span>
                    ) : (
                      <span>
                        <Num
                          value={Number(row.divergence_sigma)}
                          kind="plain"
                          dp={1}
                          step="sm"
                          colour={false}
                        />
                        <span className="jmt-rows__unit">σ</span>
                      </span>
                    )}
                  </td>
                ) : null}
                {/* `supported` is `true` here on purpose and it is not a shortcut: it is a
                    property of a candidate's *neighbours on a dimension*, and a flat row has no
                    neighbours. The family view is where an isolated pass is identified as one. */}
                <td>
                  <span className="jmt-sig-cell" title={row.blocked_by ?? undefined}>
                    <SigMark
                      mark={markFor(row.significance, true, row.execution_unresolved)}
                      tone={toneOf(row.significance, true)}
                      title={row.blocked_by ?? undefined}
                    />
                    <span className="jmt-rows__verdict">
                      {row.significance === PASS && row.confidence_pct !== null
                        ? `${Math.round(Number(row.confidence_pct))}%`
                        : phone
                          ? ''
                          : SIGNIFICANCE_WORD[row.significance] ?? ''}
                    </span>
                  </span>
                </td>
                <td>
                  {row.sparkline.length < 2 ? (
                    <span className="jmt-rows__none">{NONE}</span>
                  ) : (
                    <Sparkline
                      data={toPlot(row.sparkline)}
                      /* 56, not 72: the cell adds `--cell-pad` on both sides, so the graphic must
                         fit the track minus 24px — and this track gave 12px back to `Significance`,
                         whose one-word heading has no give. Measured, not guessed: a 72px plot asked
                         for 96px inside a 92px cell, the same overflow the phone hit at 480px. */
                      width={phone ? 40 : 56}
                      height={phone ? 14 : 22}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <CandidateDetail
        row={rows.find((candidate) => candidate.deployment_id === open) ?? null}
        onClose={() => setOpen(null)}
      />
    </div>
  );
}

/**
 * What the cohort is, stated once above the table — S14 tasks 4.7, 4.8 and 4.4.
 *
 * 🔴 **`instruments` and `book` were columns, and they were the same value on every row.** The
 * universe is a property of the *cohort*: every candidate on a rung trades the same 41 (1h) or 74
 * (4h) instruments, and `book` is the constant `core`. Two columns spending 190px to repeat one
 * fact 293 times became one line that states it once — which is also the only place it can be
 * qualified per rung, since the two rungs genuinely differ.
 *
 * ⚠ **The F-D122 caveat is HERE rather than per row (task 4.4)**, and that placement is the whole
 * decision. It qualifies every `Excess` equally, so repeating it on 293 rows would be the noise
 * this screen removes elsewhere — and a footnote below the table would be read after the column it
 * qualifies rather than before it.
 */
function CohortHeader({ items }: { items: FleetCandidate[] }): JSX.Element {
  // Derived rather than taken from the first row: the fleet is staggered (D55) and the rungs hold
  // different universes, so "the" universe does not exist as a single number.
  //
  // ⚠ **DISTINCT values per rung, not the last one seen.** `instruments` is documented as identical
  // for every candidate on a rung, so in production each rung yields exactly one number and this
  // reads `41 at 1h · 74 at 4h`. A `Map.set` per row would quietly keep whichever row came last and
  // present a disagreement as a fact — and if that contract is ever broken, the line that states
  // the universe is the one place it should be visible rather than resolved.
  const universe = useMemo(() => {
    const byRung = new Map<string, Set<number>>();
    for (const row of items) {
      const seen = byRung.get(row.timeframe) ?? new Set<number>();
      seen.add(row.instruments);
      byRung.set(row.timeframe, seen);
    }
    return [...byRung.entries()]
      .map(([rung, seen]) => [rung, [...seen].sort((a, b) => a - b)] as const)
      // Ordered by the rung's own interval rather than by its count, so the rungs read in the order
      // the fleet runs them.
      .sort((a, b) => (RUNG_ORDER.indexOf(a[0]) + 99) - (RUNG_ORDER.indexOf(b[0]) + 99));
  }, [items]);
  const books = useMemo(() => [...new Set(items.map((row) => row.book))], [items]);

  return (
    <div className="jmt-cohort">
      <p className="jmt-cohort__facts">
        Universe{' '}
        <span className="jmt-cohort__value">
          {universe.length === 0
            ? NONE
            : universe.map(([rung, ns]) => `${ns.join('/')} at ${rung}`).join(' · ')}
        </span>{' '}
        · book <span className="jmt-cohort__value">{books.join(' · ') || NONE}</span> — properties of
        the cohort, not of a row
      </p>
      {/* ⚠ **A hairline in `--loss-dim`, not a banner.** This is a standing qualification on a
          column, not an incident, and `colors.css` reserves the alert treatments for state that
          changes. */}
      <p className="jmt-cohort__caveat">
        Every <span className="jmt-cohort__value">Excess</span> is measured against an{' '}
        <strong>8-symbol</strong> bar: the controls hold 8 symbols rather than the basket (F-D122),
        so 35 intents become 8 approved.
      </p>
    </div>
  );
}

/**
 * §2's four thresholds in one cell — S14 task 4.6.
 *
 * 🔴 **Four thresholds, one question.** Trades, bars, instruments and regime are all *"is there
 * enough evidence yet"*, and they were spread across `Evidence`, a tooltip, a column that has since
 * gone, and nothing at all. One cell is what they are.
 *
 * 🔴 **Bars lead and days are demoted — task 4.3, and this is the correction that matters.** The
 * cell used to read `47d` in the same step as the trade count, which is wall clock presented as
 * evidence: a candidate whose ingestion stalled has the same day count as one that scored every
 * bar. `bars_evaluated` is what the gate actually counts, so it is what the cell says, and a
 * shortfall against `bars_expected` gets its own mark.
 *
 * ⚠ **The shortfall mark is shown and NOT explained.** `bars_evaluated < bars_expected` is the
 * ingestion-outage case; the cause could be a venue outage, a restart, or a rung that was seeded
 * late, and the screen does not know which. It reports the gap.
 */
function EvidenceCell({ row }: { row: FleetCandidate }): JSX.Element {
  const short = row.bars_evaluated < row.bars_expected;
  return (
    <span className="jmt-evi">
      <span className="jmt-evi__line">
        {/* 🔴 **Open positions sit beside the round trips, not instead of them.** `trades` counts
            closed round trips and is the right unit for §2's gate. It is the wrong unit for "is
            this candidate doing anything": measured on the live fleet 2026-08-06, 102 of 168
            deployments held 235 open positions between them and every one rendered `0t`. */}
        <Num value={row.trades} dp={0} step="sm" />
        <span className="jmt-evi__of">/{MIN_TRADES}</span>
        {row.open_positions > 0 ? (
          <span
            className="jmt-evi__open"
            title={`${row.open_positions} position${row.open_positions === 1 ? '' : 's'} still open — not yet a round trip`}
          >
            +{row.open_positions} open
          </span>
        ) : null}
      </span>
      <span className="jmt-evi__line jmt-evi__line--sub">
        <span className={short ? 'jmt-evi__bars jmt-evi__bars--short' : 'jmt-evi__bars'}>
          {row.bars_evaluated}b
        </span>
        {short ? (
          <span
            className="jmt-evi__gap"
            title={`${row.bars_expected - row.bars_evaluated} of ${row.bars_expected} bars were never scored over ${row.days} days — wall clock is not evidence`}
            aria-label="bars missing"
          >
            !
          </span>
        ) : null}
        <span className="jmt-evi__d" title={`${row.days} days of wall clock`}>
          {row.days}d
        </span>
        <span className="jmt-evi__dot">·</span>
        <span title={`${row.instruments_traded} instruments traded, of the ${MIN_INSTRUMENTS} §2 requires`}>
          {row.instruments_traded}/{MIN_INSTRUMENTS}i
        </span>
      </span>
    </span>
  );
}

/**
 * §2's fourth minimum, as three tracks — S14 task 4.1.
 *
 * 🔴 **Length is the datum and there is no hue.** Three tracks against one threshold, each carrying
 * its own count. §7's rule is that nothing on this screen may be separable by colour alone, and
 * profit/loss colour is spent on `Net` and `Excess` — so a met track differs from a short one by
 * fill length and by ink weight, and survives a greyscale screenshot.
 *
 * 🔴 **`null` renders an em dash and `{0,0,0}` renders three empty tracks.** Those are different
 * facts: the first is *"the classifier has not run"* and the second is *"it ran and this candidate
 * has traded nothing yet"*. Collapsing them would put "not measured" where "measured as zero"
 * belongs, on the column that exists because §2's fourth minimum was invisible.
 */
function RegimeCell({ row }: { row: FleetCandidate }): JSX.Element {
  const counts = row.regime_counts;
  const minimum = row.regime_minimum;
  const title = regimeTitle(counts, minimum);
  if (counts === null) {
    return (
      <span className="jmt-rows__none" title={title}>
        {NONE}
      </span>
    );
  }
  return (
    <span className="jmt-regime" title={title}>
      {REGIME_TRACKS.map(([key, letter, name]) => {
        const value = counts[key] ?? 0;
        const met = value >= minimum;
        return (
          <span
            className="jmt-regime__row"
            key={key}
            title={`${name}: ${value} of the ${minimum} trades §2 requires${met ? ' — met' : ` — ${minimum - value} short`}`}
          >
            <span className="jmt-regime__k" aria-hidden="true">
              {letter}
            </span>
            <span className="jmt-regime__track">
              <span
                className="jmt-regime__fill"
                data-met={met ? '' : undefined}
                // The one inline style in this table, and it is a datum rather than a style: the
                // fill length IS the measurement. `Math.min` because a candidate can exceed the
                // minimum and a 140%-wide fill would overflow its own track.
                style={{ width: `${Math.min(1, value / minimum) * 100}%` }}
              />
            </span>
            <span className="jmt-regime__n" data-met={met ? '' : undefined}>
              {value}
            </span>
          </span>
        );
      })}
    </span>
  );
}

/**
 * The age of the last entry and the last exit — S14 task 4.5.
 *
 * 🔴 **An age, not a clock, and the instant moves to `title`.** `13:01 / 12:13` answers *"at what
 * time"*; the question this column is asked is *"is this candidate still trading"*. The absolute
 * instant is still one hover away, in whichever timestamp mode the operator has chosen.
 *
 * 🔴 **`never` is spelled out, and on 2026-08-06 that was every row.** The fleet had placed 274
 * orders, all buys, and closed nothing — so an exit reading blank would have looked like a column
 * that had not been wired up. "No exit yet" and "no data" are different claims, and this is the one
 * place the screen can tell them apart. ⚠ Inherited from `LastActivity`, which this replaced; the
 * argument survived the column's rewrite because the fact it defends did.
 */
function ActivityAge({
  entry,
  exit,
}: {
  entry: string | null;
  exit: string | null;
}): React.JSX.Element {
  // Read here rather than threaded from the table: this is one of two renderings of the same two
  // fields, and a prop would have to travel through the row for the other one to agree with it.
  const mode = useTimestampMode();
  return (
    <span className="jmt-act">
      <span
        className="jmt-act__in"
        title={entry ? clockOf(entry, mode) : 'has never entered a position'}
      >
        {entry ? ageOf(entry) : NONE}
      </span>
      <span className="jmt-act__sep">/</span>
      <span
        className={exit ? 'jmt-act__out' : 'jmt-act__out jmt-act__out--never'}
        title={exit ? clockOf(exit, mode) : 'has never closed a position'}
      >
        {exit ? ageOf(exit) : 'never'}
      </span>
    </span>
  );
}

/**
 * One candidate, in full, opened by clicking its row.
 *
 * ## Why a modal here, when the craft floor treats one as a default to refuse
 *
 * That rule is *"a modal for a task that needs neither interruption nor protected focus"*, and
 * this one needs both halves of it. The flat table is up to 168 rows; inspecting a candidate is a
 * detour from a scan, and the operator has to come back to **the same place in the list**. A
 * detail panel below the table would move the row they were looking at; a route would lose the
 * sort and the scroll. Interruption is the point, and returning is the requirement.
 *
 * ## What it adds over the row
 *
 * The row shows nine measurements. This shows the two things the table has no column for, and
 * cannot get one:
 *
 * - **The parameters.** This is the candidate's actual configuration, and it is the answer to
 *   *why is this one different from its neighbours* — the question the whole sweep is built
 *   around. There are up to a dozen keys and they differ per family, so they can never be columns.
 * - **`blocked_by` in full.** It is the progress text for *too early* and the reason for
 *   *flagged*, and in the table it is a `title` truncated to a tooltip.
 *
 * ⚠ **The plot is 30 points and says so.** `sparkline` is what the API returns — one point a day
 * over the trailing month — and `EquityPlot`'s own contract calls it *"a correctly-styled cosmetic
 * line plot… production charts are TradingView Lightweight Charts."* It has no axis, no crosshair
 * and no zoom, so the caption states the resolution rather than letting a bigger rendering imply a
 * finer one. A per-bar equity series exists in `deployment_equity`; `GET /fleet` does not return
 * it, and drawing a smooth curve through 30 points while calling it the equity curve would be the
 * screen making a claim its data does not support.
 */
function CandidateDetail({
  row,
  onClose,
}: {
  row: FleetCandidate | null;
  onClose: () => void;
}): JSX.Element | null {
  // ⚠ **Above the early return, and it has to be.** `row === null` is the closed sheet, so a hook
  // placed after that guard would be called on some renders and not others — the rules-of-hooks
  // violation that presents as state belonging to the wrong component once the sheet is reopened.
  const timestampMode = useTimestampMode();
  // Above the early return with the hook beside it, for the same rules-of-hooks reason.
  const [pane, setPane] = useState<DetailPane>('measurements');
  // The executions table renders a different column set on a phone; see `fillColumns`.
  const phone = useIsPhone();
  // 🔴 **Fetched only when the pane is open, and keyed by candidate.**
  //
  // `enabled` is what keeps this off the wire for the two panes that do not need it — the sheet
  // opens on Measurements, and a fleet of 168 candidates browsed one at a time would otherwise
  // pull every candidate's execution history for a table nobody looked at.
  //
  // ⚠ Every hook stays **above** the `row === null` guard, for the rules-of-hooks reason the
  // comment above states: a query hook after the early return would run on some renders and not
  // others. `row?.deployment_id` and `enabled` carry the null case instead.
  const deploymentId = row?.deployment_id;
  const fills = useQuery({
    queryKey: ['fleet', 'fills', deploymentId],
    queryFn: () => api.fleet.listFleetFills({ deploymentId: deploymentId as number }),
    enabled: pane === 'fills' && deploymentId !== undefined,
  });
  // Task 3, and `enabled` for the same reason as `fills` above: the sheet opens on Measurements, so
  // browsing a fleet of 293 candidates must not pull every one's position history for a chart
  // nobody has asked to see.
  const positions = useQuery({
    queryKey: ['fleet', 'positions', deploymentId],
    queryFn: () => api.fleet.listFleetPositions({ deploymentId: deploymentId as number }),
    enabled: pane === 'positions' && deploymentId !== undefined,
  });
  if (row === null) return null;
  const closes = closeBreakdown(row.closes_by_reason);
  const points = row.sparkline.length >= 2 ? toPlot(row.sparkline) : [];
  const bounds = extremesOf(points);
  const parameters = Object.entries(row.parameters ?? {});

  return (
    <Modal
      open
      onClose={onClose}
      width={720}
      // ⚠ **`width` is an inline style on the dialog, so a media query cannot reach it** — and
      // `maxWidth: '100%'` does not save it, because a centred grid item resolves `100%` against
      // a column sized to the item itself. Measured at 430px: the dialog reported 720. `Modal`
      // spreads `style` *after* its own inline block, so this is the one hook that wins without
      // `!important`, and it stays responsive instead of being computed once at open.
      style={{ width: 'min(720px, calc(100vw - 2 * var(--space-24)))' }}
      title={`${row.candidate} · v${row.version} · ${row.timeframe}`}
    >
      <div className="jmt-detail">
        {/* ⚠ **The vendored `Modal` renders no close control** — it closes on Esc and on the
            scrim, both of which are invisible. A dialog with no visible way out is a dialog a
            touch user has to guess at, and there is no Esc key on a phone. Absolutely positioned
            against `.jmt-modal`, which is the nearest positioned ancestor. */}
        <button
          type="button"
          className="jmt-detail__close"
          onClick={onClose}
          aria-label="Close"
          title="Close"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
            strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
        <div className="jmt-detail__head">
          <StatePill state={row.state.toUpperCase() as never} />
          <span className="jmt-sig-cell">
            <SigMark
              mark={markFor(row.significance, true, row.execution_unresolved)}
              tone={toneOf(row.significance, true)}
            />
            <span className="jmt-rows__verdict">
              {row.significance === PASS && row.confidence_pct !== null
                ? `${Math.round(Number(row.confidence_pct))}% confidence`
                : SIGNIFICANCE_WORD[row.significance] ?? row.significance}
            </span>
          </span>
          <span className="jmt-detail__id">#{row.deployment_id}</span>
        </div>

        {/* `blocked_by` is the whole reason a candidate is where it is. In the table it is a
            tooltip; here it is a sentence. */}
        {row.blocked_by === null ? null : (
          <p className="jmt-detail__blocked">{row.blocked_by}</p>
        )}

        <section className="jmt-detail__plot">
          {points.length === 0 ? (
            <p className="jmt-detail__empty">
              No equity series yet &mdash; a candidate needs two closed bars before there is a
              shape to draw, which is a statement about its age rather than its result.
            </p>
          ) : (
            <>
              {/* 🔴 **The plot had no scale, so a rising line said nothing about how much.**
                  `EquityPlot` is a cosmetic renderer with no axis of its own, so the bounds are
                  drawn beside it: the high and the low of the window, in euros, against the top
                  and bottom of the plot area they correspond to.

                  ⚠ **The labels are the original decimal strings, not the plotted floats.** The
                  extremes are *located* with the numbers (a comparison, where a float is fine)
                  and then *displayed* from `sparkline` at that index, so H6's rule — money is a
                  string, do not `Number()` one — is not broken for the sake of an axis label. */}
              <div className="jmt-detail__chart">
                <div className="jmt-detail__scale">
                  <span>{money(row.sparkline[bounds.highAt])}</span>
                  <span>{money(row.sparkline[bounds.lowAt])}</span>
                </div>
                <PlotCursor points={points} sparkline={row.sparkline} width={664} height={132}>
                  <EquityPlot data={points} width={664} height={132} drawdown gridlines={3} />
                </PlotCursor>
              </div>
              {/* ⚠ **The prose caption is gone and its claim is not.** It ran three lines under
                  the plot to say the series is daily and has no axis. The axis row says the same
                  in four words, and the scale beside the plot now *shows* the bounds rather than
                  apologising for their absence — which is what made the paragraph redundant
                  rather than merely long. The operator asked for the sheet to fit one view. */}
              <div className="jmt-detail__axis">
                <span>{row.sparkline.length} daily points</span>
                <span className="jmt-detail__last">
                  latest {money(row.sparkline[row.sparkline.length - 1])}
                </span>
              </div>
            </>
          )}
        </section>

        {/* Its own surface, above the measurements — the operator's layout. What it holds is
            state rather than result, so it sits before the numbers describing how that state has
            gone, and the inset panel says so without needing a heading of its own. */}
        <section className="jmt-detail__holds">
          <span className="jmt-detail__holds-label">Holding</span>
          {row.held.length === 0 ? (
            <span className="jmt-rows__none">{NONE}</span>
          ) : (
            <span className="jmt-detail__held">
              {row.held.map((symbol) => (
                <span key={symbol} className="jmt-detail__coin">
                  <CoinMark symbol={symbol} size={18} />
                  {/* Named, not just marked. The table can lean on the mark because the row is
                      one line being scanned; the sheet is where they stopped to read. */}
                  <span>{symbol}</span>
                </span>
              ))}
            </span>
          )}
        </section>

        {/* 🔴 **Two panes, not one page.** The sheet showed measurements and parameters stacked,
            which at eleven facts plus up to five parameters ran past the fold on a laptop and read
            as a wall — the operator's verdict, and the third time this panel has been rebuilt for
            the same reason. Splitting them means the fold falls between two documents rather than
            through the middle of one.

            `TabBar` is the product's own, the same component System uses for Overview / Live log,
            so a tab here looks like a tab everywhere else. Its own rule: text tabs with a 2px
            accent underline, no pills and no boxes. */}
        {/* 🔴 **Four panes did not fit, and the fourth is the one task 3 added.** `TabBar` renders a
            `display: flex` row with `gap: var(--space-20)` and `white-space: nowrap` on every button,
            and it handles overflow nowhere — so `Measurements · Parameters · Positions · Executions`
            measured **364px inside a 316px sheet** at 412px. Three panes fitted; the fourth is what
            broke it, which is why this is fixed here rather than left as a vendored problem.

            Two changes, and `style` is spread AFTER the component's own inline block so it wins:
            shorter labels on a phone, and a 12px gap with `overflow-x: auto` as the floor — at 320px
            even the short labels are wider than the sheet, and a tab row that scrolls is honest where
            one that overflows its own container is not. */}
        <TabBar
          tabs={DETAIL_PANES.map((p) => ({ id: p.id, label: phone ? p.short : p.label }))}
          active={pane}
          onSelect={(id) => setPane(toPane(id))}
          style={phone ? { gap: 'var(--space-12)', overflowX: 'auto' } : undefined}
        />

        <section hidden={pane !== 'measurements'}>
          {/* 🔴 **Two tiers, and this time they are actually two.** The comment that stood here
              claimed "one grid, two weights" and the code had one — `Metric` and `Fact` both
              rendered `Num step="lg"`, so eleven facts arrived at identical size and the four that
              decide a promotion did not stand out from the six that give them context. That is the
              "eleven heroes and therefore none" failure its own history warns about, still present
              after two rebuilds.

              🔴 **And the orphans were structural, not spacing.** A four-column grid fed items of
              varying width leaves gaps: `Trades` filled one cell of four, `Closed by` two, `Last
              out` one — three ragged rows. Tuning spans moves the gap; it cannot remove it. So the
              second tier is not a four-column grid at all. It is label-and-value **rows**, one per
              *topic* rather than one per field, which is why `127 closed · 3 open` is one row and
              not two cells. Nothing can orphan because nothing wraps to a column. */}
          <dl className="jmt-detail__headline">
            <Metric label="Net" value={row.net_pct} kind="percent" dp={1} step="xl" colour />
            <Metric label="Sharpe" value={row.sharpe} kind="ratio" dp={2} step="xl" />
            {/* 🔴 **`Drawdown` and `vs BT` at every width, and the reason is geometry rather than
                room.** `Max drawdown` measures 109.4px against its own figure's 93.9px, and
                `vs backtest` 86.0px against 60.8px — so those two grid tracks were sized by their
                *labels* while the first two were sized by their figures. That is why the strip's
                equal gaps were equal only as **block** boxes: measured per row, the 34px figures ran
                103.7 / 103.7 / 119.2 and stopped 25.1px short of the right spine, while the block
                boxes read a tidy 103.7 / 103.7 / 103.7. The even row was not the row anyone looks at.

                Shortening these two makes every label narrower than its own figure, so every track
                is figure-pinned and track width *is* figure width: measured 117.2 / 117.2 / 117.2
                with spread 0.0 and flush at both spines. The raggedness moves to the 12px tertiary
                label row, which is the right place for it.

                ⚠ These are the flat table's own **desktop** headers (`label="Drawdown"`,
                `label="vs BT"`), so this also ends a disagreement: the sheet was naming the same two
                measurements differently from the table it opens from. Previously these were
                shortened only under `useIsPhone`, which fixed the phone and left the desktop
                mispinned. */}
            <Metric
              label="Drawdown"
              value={row.max_drawdown_pct === null ? null : String(-Number(row.max_drawdown_pct))}
              kind="percent"
              dp={1}
              step="xl"
            />
            <Metric
              label="vs BT"
              value={row.divergence_sigma}
              kind="plain"
              dp={1}
              unit="σ"
              step="xl"
            />
          </dl>

          <ExitBar closes={closes} />

          <dl className="jmt-detail__spec">
            {/* ⚠ **Open only, because the bar above now states the closed count.** These were one row
                — `127 closed · 3 open` — which was right while `Closed by` was a line of text beside it.
                Now `ExitBar`'s heading carries `127 closed` as the total its segments sum to, and
                repeating it here left the same number twice on one screen, six lines apart. The open
                count is the half the bar cannot show: a distribution of *exits* says nothing about
                what is still on. */}
            <Spec label="Positions open" value={String(row.open_positions)} />
            <Spec label="Instruments" value={`${row.instruments_traded} / ${row.instruments}`} />
            <Spec label="Running" value={`${row.days} days`} />
            {/* `never` for an exit that has not happened, `—` for an entry that has not: different
                claims, and only one of them is an absence of data. */}
            <Spec
              label="Last in / out"
              value={`${
                row.last_entry_at ? clockOf(row.last_entry_at, timestampMode) : NONE
              } / ${row.last_exit_at ? clockOf(row.last_exit_at, timestampMode) : 'never'}`}
              absent={row.last_entry_at === null && row.last_exit_at === null}
            />
            {row.blocked_by === null ? null : (
              // Promoted out of a footnote: the reason a candidate is not promotable is the most
              // actionable line on this sheet, and it used to sit below everything else.
              <Spec label="Blocked by" value={row.blocked_by} />
            )}
          </dl>
        </section>

        <section hidden={pane !== 'parameters'}>
          {parameters.length === 0 ? (
            <p className="jmt-detail__empty">
              None recorded &mdash; which is also why §8 cannot read this one as a point on any
              swept dimension.
            </p>
          ) : (
            // 🔴 **Cells here, rows in Measurements — and the difference is what else is on the
            // pane.** Both were rows, on the argument that a parameter and a measurement are the same
            // shape of fact and should not get two treatments. That held while the rows had tiles and
            // a distribution bar above them to be the quiet half *of*. On this pane they are the
            // whole content, so four hairline rows put each name ~500px from its own value across an
            // empty sheet, with nothing in between to carry the eye. The operator's word was
            // *"ugly"*, and a label separated from its value by half a screen is exactly that.
            //
            // ⚠ A cell puts the value directly under its name, which is what removes the distance
            // rather than shortening it. Same `Spec` component and same DOM — `--params` restyles the
            // container, so Measurements is untouched.
            <dl className="jmt-detail__spec jmt-detail__spec--params">
              {parameters.map(([key, value]) => (
                <Spec key={key} label={key} value={String(value)} />
              ))}
            </dl>
          )}
        </section>

        {/* 🔴 **The executions themselves — what the measurements are made of.**

            Measurements answer *"how did this candidate do"* and Parameters *"what is it"*. Neither
            answers *"what did it actually do"*, and that is the question an operator asks the
            moment a number looks wrong: which buys, at what price, and did the sell come from a
            stop or a signal. Until this pane existed the only way to see it was SQL on the box.

            ⚠ **Fills, not orders.** Most of this fleet's orders are `reduce_only` protective stops
            that rest and never trigger, so a table of orders would be mostly things that did not
            happen. A fill is the event the equity curve is made of. */}
        <section hidden={pane !== 'positions'} className="jmt-detail__positions">
          {positions.isLoading ? (
            <Skeleton />
          ) : positions.isError ? (
            <ErrorPanel message="Positions could not be loaded." />
          ) : (
            <>
              <div className="jmt-tl__head">
                <p className="jmt-tl__lead">
                  One lane per position, from <code>opened_at</code> to <code>closed_at</code>. The
                  axis is this candidate&rsquo;s own window, not the cohort&rsquo;s — D55 makes the
                  fleet staggered.
                </p>
                <p className="jmt-tl__counts">
                  {(() => {
                    const all = positions.data?.positions ?? [];
                    const open = all.filter((p) => p.closed_at === null).length;
                    const byStop = all.filter((p) => p.exit_reason === 'stop').length;
                    const bySignal = all.filter((p) => p.exit_reason === 'signal').length;
                    return `${byStop} closed by stop · ${bySignal} by signal · ${open} open`;
                  })()}
                </p>
              </div>
              <PositionTimeline
                positions={positions.data?.positions ?? []}
                truncated={positions.data?.truncated ?? false}
              />
            </>
          )}
        </section>

        <section hidden={pane !== 'fills'} className="jmt-detail__fills">
          {fills.isLoading ? (
            <Skeleton />
          ) : fills.isError ? (
            <ErrorPanel message="Executions could not be loaded." />
          ) : (
            <>
              {/* ⚠ **`compact` on a desktop, `dense` on a phone, and no `stickyFirst` anywhere.**
                  `dense` is 6px of cell padding against `compact`'s 8 — 20px back across five
                  columns, which is most of the 22px the phone layout was over by once Fee was
                  kept. On the wide sheet there is nothing to buy, and dense's 26px rows and
                  vertical hairlines are what made the first version read as cramped.

                  ⚠ **Density is the lever because padding is an inline style.** `DataTable` writes
                  `padding: 0 {d.pad}px` on every cell, so a stylesheet cannot reach it without
                  `!important` — the same trap as `Num`'s inline font-size. The prop is the
                  supported way to ask, and asking through the component keeps this table looking
                  like every other one. */}
              <DataTable
                density={phone ? 'dense' : 'compact'}
                columns={fillColumns(phone)}
                rows={(fills.data?.fills ?? []).map((fill, index) => ({
                  ...fill,
                  // The API has no fill id and adding one would widen the payload for a key. The
                  // list is ordered and stable within a response, so the index is the identity.
                  id: index,
                }))}
                empty={
                  <>
                    Nothing has executed yet &mdash; this candidate has produced no fills, which is
                    why its measurements are mostly em dashes.
                  </>
                }
              />
              {/* ⚠ **Says so when the page is not the whole record.** `total` is counted
                  separately from the page precisely so this line can be true; inferring
                  completeness from the row count would render a truncated table as everything. */}
              {(fills.data?.total ?? 0) > (fills.data?.returned ?? 0) ? (
                <p className="jmt-detail__empty">
                  Showing the most recent {fills.data?.returned} of {fills.data?.total} executions.
                </p>
              ) : null}
            </>
          )}
        </section>
      </div>
    </Modal>
  );
}

/**
 * The executions table's columns, for a viewport.
 *
 * 🔴 **A function of `phone`, not a constant with CSS hiding the extras.** `useIsPhone`'s own
 * docstring is the reason and it was learned three times: *"hiding a table column in CSS does not
 * remove it"* — a `<col>` keeps its width when every cell in its column is `display: none`, which
 * is how a phone table once measured 644px inside a 430px viewport. A table's column set is
 * structure, and CSS is the wrong tool for changing structure.
 *
 * ⚠ **Money is right-aligned and mono, identifiers left.** The design's rule, and the reason is
 * scanning: a column of figures is compared down its digits.
 */
function fillColumns(phone: boolean): DataTableColumn[] {
  return [
    {
      key: 'ts',
      label: 'When',
      mono: true,
      // ⚠ On a phone the date is dropped and only the clock is kept. `clockOf` renders
      // `08-07 21:00` for anything before today, which is the widest cell in the table at ~75px —
      // and on a sheet with ~280px of usable width that is a quarter of it spent on a year every
      // row shares. The full timestamp is one tap away on a wider screen.
      render: (row: DataTableRow) => {
        const full = clockOf(String(row.ts), 'local');
        return phone ? full.slice(-5) : full;
      },
    },
    {
      key: 'symbol',
      // 🔴 **No header on a phone, because the header was the column's width.** The cell is a 16px
      // mark; the word `INSTRUMENT` is ~90px, so the heading alone was setting the track and the
      // icon sat in the middle of six times its own width. A `<th>` sizes its column whether or
      // not the cells need it — the same lesson as `<col>` in `useIsPhone`, one element along.
      //
      // ⚠ The accessible name does not go with it: every cell carries `aria-label` with the
      // ticker, so the column is unlabelled to the eye and named to a screen reader.
      label: phone ? '' : 'Instrument',
      // 🔴 **The mark, and on a phone the mark alone.** `CoinMark` is self-hosted and falls back
      // to a monogram tile for the one instrument in 74 with no logo — *"the fallback is the
      // design, not the exception"* — so a logo-only column never degrades to a broken glyph.
      //
      // ⚠ The ticker stays in `aria-label` when the text is hidden. A logo is not an accessible
      // name, and a screen reader on the phone layout would otherwise read the row without ever
      // saying which instrument it is about.
      render: (row: DataTableRow) => (
        <span className="jmt-fill__instrument" aria-label={String(row.symbol)}>
          <CoinMark symbol={String(row.symbol)} size={phone ? 16 : 18} />
          {phone ? null : String(row.symbol)}
        </span>
      ),
    },
  {
      key: 'side',
      label: 'Side',
      // ⚠ **No colour on this column, and that is a rule rather than a preference.** The three
      // colour laws are product truth: `--profit`/`--loss` mean profit and loss, and an entry is
      // neither; `--accent` means you can interact with this, and this is a state. So entry and
      // exit are distinguished by the word, which is the one an operator would say out loud.
      //
      // 🔴 **The side is shown only when it disagrees with the direction, and that is the whole
      // point of the column.** This venue is spot-only (D41), so an entry is a buy and an exit is
      // a sell — printing `entry · buy` on every row repeats itself and is what made this read as
      // noisy. When they *do* disagree the row is an F-D84 phantom: a `reduce_only` fill that
      // opened a position rather than closing one, which is how two shorts and a long reached the
      // live fleet. Quiet in the ordinary case, loud in the one worth seeing.
      //
      // ⚠ **`gapped` moved here when Liquidity was removed.** It is rare and it matters when it
      // happens — *"a stop that 'worked' on a gap did not work"* — so dropping its column had to
      // mean rehoming the flag, not losing it. This column is already the row's *what kind of
      // event was this*, which is the question a gap answers badly.
      // ⚠ **On a phone this column carries at most ONE flag, and the priority is deliberate.**
      // Both can be true at once — a phantom that also gapped renders `exit · buy · gapped`, which
      // is ~110px in a column that has 76px at a 360px viewport. That row is not in the fixture,
      // which is exactly why it had to be reasoned about rather than measured: the widest case a
      // fixture omits is the one that overflows in production.
      //
      // The side mismatch wins because the two flags are different kinds of fact. A mismatch means
      // the **platform did something impossible** — a reduce-only fill that opened a position, an
      // F-D84 phantom. A gap means the **market moved through a stop**, which is ordinary market
      // behaviour with a bad outcome. When only one fits, the bug outranks the weather. The wide
      // layout shows both.
      render: (row: DataTableRow) => {
        const closing = Boolean(row.reduce_only);
        const parts = [closing ? 'exit' : 'entry'];
        const mismatched = String(row.side) !== (closing ? 'sell' : 'buy');
        if (mismatched) parts.push(String(row.side));
        if (row.gapped && !(phone && mismatched)) parts.push(phone ? 'gap' : 'gapped');
        return parts.join(' · ');
      },
    },
    // ⚠ **Quantity and price are deliberately absent: they were the width.**
    // `9541984732.00000000` against a price of `2.62e-8` is a real position, and rendering it
    // honestly took 753px of a 706px sheet.
    //
    // What is lost is the **venue-level** detail — the exact price a fill printed at. What is kept
    // is the money, because `notional` is quantity × price, so *how big was this* survives. The
    // full precision is still one API call away (`/fleet/{id}/fills` returns both), so this is a
    // display choice and not a discarded record.
    //
    // ⚠ **An earlier version of this comment claimed those columns had to bypass `Num` because
    // `formatNum` would render a small price as `€0.00`. That was wrong**, and the correction is
    // worth keeping because it is easy to re-derive: `formatNum`'s second line is
    // `if (typeof value === 'string') return value`, so **every money value in this product — all
    // of which are strings under H6 — passes through untouched**. `dp`, `kind` and the currency
    // prefix apply to *numbers* only. The 2dp-and-`Math.abs` path this pane was being protected
    // from is unreachable from here.
    {
      key: 'notional',
      label: phone ? '€' : 'Notional (€)',
      align: 'right',
      mono: true,
      render: notionalCell,
    },
    { key: 'fee', label: phone ? 'Fee' : 'Fee (€)', align: 'right', mono: true, render: feeCell },
  ];
}

/**
 * 🔴 **Money to two decimals, and `<Num dp={2}>` cannot do it.**
 *
 * `formatNum`'s second line is `if (typeof value === 'string') return value` — and every money
 * value in this product is a string under H6 — so `dp`, `kind` and the currency prefix apply to
 * *numbers* only. A money column rendered straight through `Num` prints whatever the API sent.
 *
 * What the API sends is not tidy. `notional` is `quantity × price` at full `Decimal` precision and
 * `fee` comes off the venue the same way; measured against production on 2026-08-08:
 *
 *     notional  10.7261677440000000      (16 decimals)
 *     fee        0.010726167744000000    (18 decimals)
 *
 * The fixture's clean `419.02` hid this completely, which is why it read as fine on the preview
 * and wrong on the box.
 *
 * ⚠ **`Number()` on money, which the client forbids — the same sanctioned exception as `toPlot`,
 * on the same argument.** The rule is about *arithmetic*: *"a price that loses its last digits
 * produces a wrong order size."* Nothing is computed from this; it is a column width. Rounding
 * rather than slicing the string, because a truncated `0.019` would be a fee nobody paid.
 *
 * ⚠ **A fee below half a cent renders `0.00`**, and that is the cost of the operator's two-decimal
 * request rather than an oversight: at this fleet's sizes most fees are sub-cent. The exact figure
 * is in `/fleet/{id}/fills` and in `fills.fee`.
 */
function money2(value: unknown): string {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : String(value ?? '');
}

function notionalCell(row: DataTableRow): JSX.Element {
  return <Num value={money2(row.notional)} kind="money" />;
}

/** Four places, for the fleet-wide table where sub-cent fees are the norm and the width exists. */
function money4(value: unknown): string {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(4) : String(value ?? '');
}

function feeCell(row: DataTableRow): JSX.Element {
  return <Num value={money2(row.fee)} kind="money" />;
}

/**
 * Where the window's high and low sit  ];
}

/**
 * Where the window's high and low sit, as **indices** into the series.
 *
 * ⚠ Indices rather than values, and that is the whole point of the function. The comparison is
 * done on the plotted floats, which is safe — an ordering does not care about the last digit —
 * and the *label* is then read from the original decimal string at that index, so no money is
 * ever formatted from a float. H6: money is a string; do not `Number()` one.
 */
function extremesOf(points: number[]): { highAt: number; lowAt: number } {
  let highAt = 0;
  let lowAt = 0;
  for (const [i, value] of points.entries()) {
    if (value > (points[highAt] as number)) highAt = i;
    if (value < (points[lowAt] as number)) lowAt = i;
  }
  return { highAt, lowAt };
}

/**
 * A decimal string as euros, without ever turning it into a float.
 *
 * `Intl` on the string's own digits: the integer part gets thousands separators and the fraction
 * is truncated to two places by slicing rather than rounding, because rounding a balance for a
 * label is the kind of quiet arithmetic H6 exists to prevent.
 */
function money(value: string | undefined): string {
  if (value === undefined) return NONE;
  const [whole = '0', fraction = ''] = value.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `€${grouped}${fraction === '' ? '' : `.${fraction.slice(0, 2)}`}`;
}

/** One measurement. `null` renders the em dash `Num` already owns, never a zero. */
/**
 * One supporting fact: a small label with a small value beside it.
 *
 * Not a `Metric`. The distinction is the point — `Metric` is for the four figures that decide
 * whether a candidate is promoted, and rendering eleven things at that weight made the sheet a
 * wall in which none of them stood out. These are the context those four are read against.
 *
 * `absent` takes the tertiary ink this product reserves for "no measurement", which is what
 * `never` and `—` both are.
 */
/** A label and a value at the measurement type. Used for anything that is not a `Num`. */
/**
 * How the closed positions closed, as a proportional bar with a legend.
 *
 * ## 🔴 Why this stopped being a line of text
 *
 * It was one right-aligned run — `signal 90 · stop 30 · take-profit 5 · risk engine 2` — and the
 * operator's verdict was that it looked awful. They were right, and the reason is that it was the
 * wrong *kind* of thing in a row: every other spec row holds **one** value, and this holds a
 * **distribution**. Four numbers set in a line have to be read and then divided in your head before
 * they say anything; the one question they answer is *what proportion of the exits were stops*, and
 * a proportion is what a bar shows without arithmetic.
 *
 * ⚠ **And it is the most decision-relevant fact on the sheet**, which is why it moves out of the
 * quiet rows entirely and sits directly under the four headline figures. A candidate whose return
 * came from stops is not the thing its backtest described, however good the Sharpe above it looks.
 * Until F-D82 the engine recorded every close as `signal`, so cohort 1's breakdown is *wrong* rather
 * than merely absent — one more reason to draw it where it cannot be skimmed past.
 *
 * ## What it does when there is nothing to draw
 *
 * 🔴 **`—`, not an empty bar.** Nothing closed is a measurement never taken, and an empty track
 * would read as "closed, in no particular way". This cohort spent its first bar in exactly that
 * state — 67 positions open, none closed.
 */
function ExitBar({
  closes,
}: {
  closes: ReadonlyArray<{ key: string; label: string; count: number }>;
}): JSX.Element {
  const total = closes.reduce((sum, entry) => sum + entry.count, 0);
  return (
    <section className="jmt-exit">
      <div className="jmt-exit__head">
        <span className="jmt-exit__label">Closed by</span>
        {total === 0 ? null : (
          <span className="jmt-exit__total">
            <Num value={0} step="sm" colour={false}>
              {`${total} closed`}
            </Num>
          </span>
        )}
      </div>
      {total === 0 ? (
        <p className="jmt-detail__empty">
          {NONE} &mdash; nothing has closed yet, so there is no distribution to read.
        </p>
      ) : (
        <>
          {/* ⚠ **`flex-grow` on the count, not a percentage width.** A percentage of a rounded track
              leaves sub-pixel seams between segments at some totals, and 127 exits over four reasons
              is exactly the sort of division that lands on them. Flex distributes the remainder
              itself, so the segments always sum to the track. */}
          <div
            className="jmt-exit__track"
            role="img"
            aria-label={closes.map((e) => `${e.label} ${e.count}`).join(', ')}
          >
            {closes.map((entry) => (
              <span
                key={entry.key}
                className="jmt-exit__seg"
                style={{ flexGrow: entry.count, background: exitReasonFill(entry.key) }}
              />
            ))}
          </div>
          {/* The legend carries the counts, so the bar itself needs no labels inside it — which is
              what lets it be 8px tall rather than tall enough to hold text. */}
          <ul className="jmt-exit__key">
            {closes.map((entry) => (
              <li key={entry.key} className="jmt-exit__item">
                <span
                  className="jmt-exit__swatch"
                  style={{ background: exitReasonFill(entry.key) }}
                  aria-hidden="true"
                />
                <span className="jmt-exit__name">{entry.label}</span>
                <Num value={0} step="sm" colour={false}>
                  {String(entry.count)}
                </Num>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/**
 * One supporting fact as a **row**: label on the left, value on the right.
 *
 * 🔴 **A row rather than a grid cell, and that is the fix rather than a preference.** The previous
 * version put these in the same four-column grid as the headline figures, which orphaned every item
 * whose width did not divide into four — `Trades` filling one cell of four, `Last out` alone on its
 * own line. A gap in a grid can be moved by changing a span; it cannot be removed. A row has no
 * columns to leave empty.
 *
 * ⚠ **`--num-sm`, deliberately below the headline's `xl`.** This is the context the four decision
 * figures are read *against*, and rendering it at the same size is what gave this sheet eleven
 * heroes and therefore none — twice, by its own history.
 */
function Spec({
  label,
  value,
  absent = false,
}: {
  label: string;
  value: string;
  absent?: boolean;
}): JSX.Element {
  return (
    <div className="jmt-detail__specrow">
      <dt>{label}</dt>
      <dd>
        <Num value={absent ? null : 0} step="sm" colour={false}>
          {value}
        </Num>
      </dd>
    </div>
  );
}

function Metric({
  label,
  value,
  kind,
  dp,
  unit,
  step = 'lg',
  colour = false,
}: {
  label: string;
  value: string | null;
  kind: 'percent' | 'ratio' | 'plain';
  dp: number;
  unit?: string;
  /**
   * Which rung of `Num`'s ramp.
   *
   * ⚠ **A prop rather than a stylesheet rule, and that is forced.** `Num` sets `font-size` as an
   * *inline* style, so a CSS rule cannot change it without `!important` — this file's own history
   * records a `--num-md` rule that silently did nothing for months for exactly that reason.
   */
  step?: 'xl' | 'lg' | 'md' | 'sm';
  colour?: boolean;
}): JSX.Element {
  return (
    <div className="jmt-detail__metric">
      <dt>{label}</dt>
      <dd>
        <Num
          value={value === null ? null : Number(value)}
          kind={kind}
          dp={dp}
          step={step}
          colour={colour}
        />
        {unit !== undefined && value !== null ? (
          <span className="jmt-rows__unit">{unit}</span>
        ) : null}
      </dd>
    </div>
  );
}

/**
 * A sortable header cell.
 *
 * ⚠ **The arrow is always drawn, dimmed until the column is active.** The vendored header hides
 * it at `opacity: 0`, so the table gave no sign it could be sorted until after somebody had
 * already sorted it — an affordance you have to discover by accident is not one.
 */
/**
 * How many coin marks fit before the column stops being scannable. Tighter on a phone.
 *
 * 🔴 **Two on a phone, down from three — S14 task 2, and it is a width measurement rather than a
 * taste judgement.** A mixed long/short holding renders each short as a 14px mark PLUS its own `▾`
 * caret, so three marks with two carets and a `+1` counter measured **89px inside an 85px cell** at
 * 412px — and at 390px the overflow pushed the whole table 8px past its own scroller, which is the
 * *"an `overflow-x: auto` table does not overflow, it hides the answer"* failure. Two marks and a
 * `+2` say the same thing in 72px.
 *
 * 🔴 **Three on the desktop too, down from four — measured on the LIVE fleet, not on a fixture.**
 * The operator's screenshot showed the `+N` counter of the two `SellAndHold` controls printing
 * straight over the `Age` column. The arithmetic: a long is one 16px mark overlapping its neighbour
 * by 5px, so ~11px each; a short is a mark PLUS its own `▾` caret PLUS inline margins, ~28px each.
 * Four shorts and a counter therefore ask for ~160px, and the track was 108. Widening it to 142 pays
 * for three; the fourth is what `+N` is for.
 */
const HELD_SHOWN = 3;
const HELD_SHOWN_PHONE = 2;

/**
 * The instruments a candidate is holding right now.
 *
 * ⚠ **Marks plus a count, never marks alone.** Four 16px logos read as "some coins" at a glance
 * and the useful question is *how many* — which is also the number that has to agree with the
 * `+N open` in Evidence beside it. Overflow collapses to `+N` rather than wrapping, because a
 * row that grows to two lines when a strategy opens a fifth position makes the whole table jump.
 */
/**
 * `EquityPlot`'s own inner padding, in viewBox units.
 *
 * 🔴 **Mirrored from the vendored component, and the value is 8.** This was 4, guessed rather
 * than read, which put the dot below the curve — visible immediately and invisible to tsc,
 * oxlint and every test. If `EquityPlot.jsx` ever changes its `pad`, this drifts silently; the
 * only guard is that both numbers are one grep apart.
 */
const PLOT_PAD = 8;

/**
 * A hover crosshair over the equity plot.
 *
 * ⚠ **An overlay, not a change to `EquityPlot`.** That component is vendored and under
 * `ops/design_manifest.py`'s checksum, so editing it fails the build — and it is documented as a
 * *"cosmetic renderer"* with no axis and no interaction, which is a deliberate boundary rather
 * than an omission. Chrome is app-owned; the vendored data components are inherited as they are.
 *
 * ## 🔴 The geometry has to be converted, not assumed, and getting it wrong twice proved it
 *
 * `EquityPlot` draws into a **viewBox** of `width × height` and renders it at `width="100%"`
 * with `preserveAspectRatio="none"`. So the drawing is *stretched* to whatever box the panel
 * gives it — measured here at **559×129 against a nominal 664×132** — and screen pixels are not
 * viewBox units. Two separate defects came out of ignoring that:
 *
 * 1. Deriving the step from the nominal `width` while the element was narrower. The rule
 *    trailed the pointer by a growing margin and the last five points were unreachable.
 * 2. Then, having measured the box, treating measured pixels *as* viewBox units and using a
 *    padding of 4 where the component uses 8 — which left the dot sitting below the curve.
 *
 * Both typechecked and linted. The first was caught by `getBoundingClientRect`, the second by
 * the operator looking at it. So the mapping now goes through viewBox space explicitly in both
 * directions rather than being approximated in either.
 *
 * 🔴 **The readout is the original decimal string, never the plotted float.** `points` exists
 * only to locate the index under the pointer — a comparison, where a float is harmless — and the
 * euro figure comes back out of `sparkline` at that index. H6's rule is that money is a string
 * and nothing may `Number()` one for display; an axis label is not an exception.
 */
function PlotCursor({
  points,
  sparkline,
  width,
  height,
  children,
}: {
  points: number[];
  sparkline: string[];
  /** The viewBox width handed to `EquityPlot`, not the rendered width. */
  width: number;
  /** The viewBox height handed to `EquityPlot`. */
  height: number;
  children: React.ReactNode;
}): JSX.Element {
  // The pointer offset AND the box it was measured in. Storing the offset alone would reintroduce
  // the first defect above the moment the panel resized between a move and a render.
  const [at, setAt] = useState<{ x: number; w: number; h: number } | null>(null);

  const box = at ?? { x: 0, w: width, h: height };
  const last = points.length - 1;
  const low = Math.min(...points);
  const high = Math.max(...points);
  const span = high - low || 1;

  /** viewBox x for point `i`, exactly as `EquityPlot.X`. */
  const vbX = (i: number) => PLOT_PAD + (i / (last || 1)) * (width - PLOT_PAD * 2);
  /** viewBox y for value `v`, exactly as `EquityPlot.Y`. */
  const vbY = (v: number) => PLOT_PAD + (1 - (v - low) / span) * (height - PLOT_PAD * 2);

  // Pointer pixels → viewBox units → index. The inverse of `vbX`.
  const index =
    at === null
      ? null
      : Math.max(
          0,
          Math.min(
            last,
            Math.round((((at.x / box.w) * width - PLOT_PAD) / (width - PLOT_PAD * 2)) * last),
          ),
        );

  // viewBox units → pointer pixels, for drawing.
  const x = index === null ? 0 : (vbX(index) / width) * box.w;
  const y = index === null ? 0 : (vbY(points[index] ?? low) / height) * box.h;

  return (
    <div
      className="jmt-plotc"
      onPointerMove={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setAt({ x: event.clientX - rect.left, w: rect.width, h: rect.height });
      }}
      onPointerLeave={() => setAt(null)}
      // Pointer-driven only. There is no keyboard equivalent because the same numbers are
      // already reachable without it: the scale beside the plot gives the high and the low, and
      // the axis row below gives the count and the latest.
      aria-hidden="true"
    >
      {children}
      {index !== null ? (
        <>
          <span className="jmt-plotc__rule" style={{ left: x }} />
          <span className="jmt-plotc__dot" style={{ left: x, top: y }} />
          <span
            className="jmt-plotc__read"
            // Flips side near the right edge so the readout never leaves the panel.
            style={x > box.w * 0.68 ? { right: box.w - x + 8 } : { left: x + 8 }}
          >
            <span className="jmt-plotc__value">{money(sparkline[index])}</span>
            <span className="jmt-plotc__at">
              {index + 1}/{points.length}
            </span>
          </span>
        </>
      ) : null}
    </div>
  );
}

/**
 * What a candidate is holding, and **on which side** — cohort 4 §7.
 *
 * 🔴 **`held` alone made a long and a short indistinguishable**, on the one screen that decides
 * which candidate gets real money. With shorts in the cohort that is a row asserting something
 * false, which is the shape of four week-one findings.
 *
 * ⚠ **The side is a GLYPH, never a colour.** The design system's law is that the accent is never a
 * state or a mode, and §7 says so outright — *"do not encode side with colour."* A downward caret
 * after the coin says short; its absence says long. So the distinction survives a greyscale
 * screenshot, a colour-blind reader, and the amber accent being reserved for its three real uses.
 *
 * ⚠ **The shorts are listed FIRST**, because the truncation at `HELD_SHOWN` would otherwise hide
 * exactly the half that is new — a candidate holding six longs and one short would show six coins
 * and `+1`, and the `+1` is the interesting one.
 */
function HeldCoins({
  held,
  heldShort = [],
  phone = false,
}: {
  held: readonly string[];
  heldShort?: readonly string[];
  phone?: boolean;
}): React.JSX.Element {
  if (held.length === 0) {
    // Not an empty cell. `—` is this product's mark for "nothing here", and an empty cell is
    // indistinguishable from a column that failed to render.
    return <span className="jmt-rows__none">{NONE}</span>;
  }
  const short = new Set(heldShort);
  // Shorts first, then longs, each in the order they arrived (the query sorts them). Stable, so two
  // renders of the same row cannot disagree.
  const ordered = [...held.filter((s) => short.has(s)), ...held.filter((s) => !short.has(s))];
  const shown = ordered.slice(0, phone ? HELD_SHOWN_PHONE : HELD_SHOWN);
  const rest = ordered.length - shown.length;
  const label = ordered.map((s) => (short.has(s) ? `${s} (short)` : s)).join(', ');
  return (
    <span className="jmt-held" title={label}>
      {shown.map((symbol) =>
        short.has(symbol) ? (
          <span className="jmt-held__side" key={symbol} title={`${symbol} — short`}>
            <CoinMark symbol={symbol} size={phone ? 14 : 16} />
            {/* `aria-hidden` because the accessible name is already on the wrapper's `title`, and
                a screen reader announcing a bare caret between coin names is noise. */}
            <span className="jmt-held__caret" aria-hidden="true">
              ▾
            </span>
          </span>
        ) : (
          <CoinMark key={symbol} symbol={symbol} size={phone ? 14 : 16} />
        ),
      )}
      {rest > 0 ? <span className="jmt-held__more">+{rest}</span> : null}
    </span>
  );
}




function Th({
  col,
  label,
  align,
  group,
  title,
  sortKey,
  sortDir,
  onSort,
}: {
  col: string;
  label: string;
  align?: boolean;
  group?: boolean;
  /**
   * What the column means, for the headings that are not self-describing.
   *
   * ⚠ **On the `<th>` rather than on the sort button**, because it describes the column and not the
   * control: a tooltip on the button reads as an explanation of what clicking will do. S14 task 2
   * merged two columns into `Risk` and narrowed `Age in / out` to two units, and neither heading
   * carries its own definition any more.
   */
  title?: string;
  sortKey: string;
  sortDir: 'asc' | 'desc';
  onSort: (key: string) => void;
}): JSX.Element {
  const on = sortKey === col;
  return (
    <th
      scope="col"
      className={align ? 'jmt-rows__n' : undefined}
      data-group={group ? '' : undefined}
      title={title}
      aria-sort={on ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className="jmt-rows__sort" data-on={on ? '' : undefined} onClick={() => onSort(col)}>
        {label}
        <span aria-hidden="true" className="jmt-rows__arrow" data-dir={on ? sortDir : undefined}>
          ▾
        </span>
      </button>
    </th>
  );
}

/** The state named in a word where there is no confidence figure to show instead. */
const SIGNIFICANCE_WORD: Readonly<Record<string, string>> = {
  flagged: 'flagged',
  too_early: 'too early',
  not_ranked: 'not ranked',
};

/** One numeric column's value, or `null` when there is no measurement. */
function numeric(row: FleetCandidate, key: string): number | null {
  switch (key) {
    case 'days':
      return row.days;
    case 'trades':
      return row.trades;
    // Sorted on the instant, not the rendered string: `08-05 22:10` sorts before `09:12` as
    // text and after it in time, which is the wrong order on exactly the rows an operator
    // sorts this column to find.
    case 'last_entry_at':
      return row.last_entry_at === null ? null : Date.parse(row.last_entry_at);
    case 'net_pct':
      return row.net_pct === null ? null : Number(row.net_pct);
    // Signed, unlike `divergence_sigma` below: this column asks *"did it beat its control"*, and
    // the sign IS the answer. Sorting on the absolute value would file the worst candidates
    // beside the best.
    case 'excess_pct':
      return row.excess_pct === null ? null : Number(row.excess_pct);
    case 'sharpe':
      return row.sharpe === null ? null : Number(row.sharpe);
    case 'max_drawdown_pct':
      return row.max_drawdown_pct === null ? null : Number(row.max_drawdown_pct);
    case 'divergence_sigma':
      // Sorted on the ABSOLUTE divergence: the column asks "how far from its backtest", and +3σ
      // and −3σ are equally alarming. Sorting signed would put the most suspicious candidates at
      // opposite ends of the same list.
      return row.divergence_sigma === null ? null : Math.abs(Number(row.divergence_sigma));
    default:
      return null;
  }
}

/**
 * One lane per position, from `opened_at` to `closed_at` — S14 task 3.
 *
 * ## 🔴 Why this is a chart and not a fourth table
 *
 * `Executions` already lists what happened, row by row, and it cannot answer the question this pane
 * exists for: *were these positions held at the same time, or one after another*. Eight rows of
 * timestamps make a reader do interval arithmetic in their head; eight lanes on a shared axis make
 * overlap the first thing visible. Concurrency is the property, so geometry is the rendering.
 *
 * ## 🔴 Nothing here is hue-coded, and that is §7 rather than restraint
 *
 * Side is **geometry**: a long lane is filled, a short lane is outlined and carries the row's own
 * `▾` caret — the same caret `HeldCoins` uses, so one mark means "short" everywhere on this screen.
 * How a position ended is geometry too: a bar terminal for a stop, a chevron for a signal, and an
 * open arrowhead at the now-rule for one still open. The chart survives a greyscale screenshot, and
 * profit/loss colour stays on `Net` and `Excess` where it means money.
 *
 * ⚠ **An open lane ends in an arrowhead at the now-rule, never in a terminal.** A lane that simply
 * stopped would read as a close, which is the one thing this chart must never say wrongly — and the
 * reason it is built on `positions` rather than on paired `fills` (F-D82).
 *
 * ## ⚠ The axis is the candidate's own window, not the cohort's
 *
 * `days` on the row is this candidate's clock, and D55 makes the fleet staggered — so a shared
 * six-week axis would draw a candidate seeded in week four as three quarters of empty lane. The
 * window runs from the oldest `opened_at` in the page to now, which is also why `truncated` has to
 * be visible: a clipped page would silently move the left edge of the axis.
 */
function PositionTimeline({
  positions,
  truncated,
}: {
  positions: FleetPosition[];
  truncated: boolean;
}): JSX.Element {
  const mode = useTimestampMode();
  const geometry = useMemo(() => {
    if (positions.length === 0) return null;
    const now = Date.now();
    const opens = positions.map((p) => Date.parse(p.opened_at));
    const first = Math.min(...opens);
    // A candidate whose only position opened moments ago would otherwise divide by zero and draw
    // every lane at x = NaN. One hour is the smallest window this chart is legible at.
    const span = Math.max(now - first, 3_600_000);
    return { now, first, span };
  }, [positions]);

  if (positions.length === 0) {
    return (
      <p className="jmt-detail__empty">
        No positions yet &mdash; this candidate has not entered one, which is why its measurements
        are mostly em dashes.
      </p>
    );
  }
  const { now, first, span } = geometry!;

  // 🔴 **Oldest at the TOP, so the lanes descend in the order the positions were opened.** The API
  // returns newest first, which is right for a list and wrong for a time chart: a reader scans a
  // Gantt from the top-left, and reversing here means the first lane is also the left-most opening.
  const lanes = [...positions].reverse();
  const LANE = 30;
  const PAD_TOP = 14;
  const height = lanes.length * LANE + PAD_TOP + 22;
  const pct = (t: number): number => ((t - first) / span) * 100;

  // Week gridlines, at the candidate's own week boundaries rather than the cohort's.
  const weeks: number[] = [];
  for (let w = 0; first + w * 604_800_000 <= now; w += 1) weeks.push(w);

  return (
    <div className="jmt-tl">
      <div className="jmt-tl__plot" style={{ height: `${height}px` }}>
        {/* 🔴 **The rules live in their own box, inset by the same two gutters the lanes are.**
            Percentages on an absolutely-positioned child resolve against the containing block's
            PADDING box — so with the gutters expressed as padding on the plot, a rule at `50%` landed
            half way across the symbol column and the whole axis was offset from the lanes it was
            supposed to measure. Both this box and every lane's track are sized from
            `--tl-gutter-l/r`, so they agree by construction rather than by two numbers being kept in
            step by hand. */}
        <div className="jmt-tl__axis">
          {weeks.map((w) => (
            <span
              className="jmt-tl__week"
              key={w}
              style={{ left: `${pct(first + w * 604_800_000)}%` }}
            >
              <span className="jmt-tl__weeklabel">w{w}</span>
            </span>
          ))}
          <span className="jmt-tl__now" style={{ left: '100%' }}>
            <span className="jmt-tl__nowlabel">now</span>
          </span>
        </div>

        {lanes.map((p, index) => {
          const open = p.closed_at === null;
          const x0 = pct(Date.parse(p.opened_at));
          const x1 = open ? 100 : pct(Date.parse(p.closed_at as string));
          const short = p.side === 'short';
          return (
            <span
              className="jmt-tl__lane"
              key={p.position_id}
              style={{ top: `${PAD_TOP + index * LANE}px` }}
            >
              <span className="jmt-tl__sym">
                {p.symbol.replace('/EUR', '')}
                {short ? (
                  <span className="jmt-tl__caret" title={`${p.symbol} — short`}>
                    ▾
                  </span>
                ) : null}
              </span>
              <span className="jmt-tl__track">
                <span
                  className={short ? 'jmt-tl__bar jmt-tl__bar--short' : 'jmt-tl__bar'}
                  data-open={open ? '' : undefined}
                  style={{ left: `${x0}%`, width: `${Math.max(0.4, x1 - x0)}%` }}
                  title={timelineTitle(p, mode)}
                />
                {/* The opening tick is drawn for every lane: an entry is a fact whatever became of
                    it, and without it a very short position is a mark with no visible start. */}
                <span className="jmt-tl__open" style={{ left: `${x0}%` }} />
                <span
                  className={`jmt-tl__end jmt-tl__end--${open ? 'open' : p.exit_reason === 'stop' ? 'stop' : 'signal'}`}
                  style={{ left: `${x1}%` }}
                  title={
                    open
                      ? 'still open — the lane ends at the now-rule, not at a close'
                      : `closed by ${p.exit_reason}`
                  }
                />
              </span>
              {/* 🔴 **`—` for an open position, never `€0`.** `realised_gross` is `null` while a
                  position is open because the column cannot say "nothing realised yet", and a zero
                  here would read as a settled flat result. */}
              <span className="jmt-tl__pnl">
                {p.realised_gross === null ? NONE : signedMoney(p.realised_gross)}
              </span>
            </span>
          );
        })}
      </div>

      <ul className="jmt-tl__legend">
        <li>
          <span className="jmt-tl__key jmt-tl__key--long" /> long
        </li>
        <li>
          <span className="jmt-tl__key jmt-tl__key--short" /> <span aria-hidden="true">▾</span> short
        </li>
        <li>
          <span className="jmt-tl__key jmt-tl__key--stop" /> closed by stop
        </li>
        <li>
          <span className="jmt-tl__key jmt-tl__key--signal" /> closed by signal
        </li>
        <li>
          <span className="jmt-tl__key jmt-tl__key--open" /> still open
        </li>
      </ul>

      <p className="jmt-tl__note">
        Side is geometry: a long lane is <strong>filled</strong>, a short lane is{' '}
        <strong>outlined and carries its own ▾ caret</strong>. Nothing here is hue-coded — the chart
        survives a greyscale screenshot, and profit/loss colour stays on Net and Excess. An open lane
        ends in an <strong>open arrowhead at the now-rule</strong>, never in a terminal, so it cannot
        be mistaken for a close.
      </p>

      {/* ⚠ **Says so when the page is not the whole record**, like the Executions pane — and it
          matters more here, because a clipped page moves the left edge of the axis rather than just
          hiding rows. */}
      {truncated ? (
        <p className="jmt-detail__empty">
          Older positions were clipped by the window, so the axis starts at the oldest position shown
          rather than at this candidate&rsquo;s first.
        </p>
      ) : null}
    </div>
  );
}

/**
 * A realised result, signed the way the rest of the product signs one.
 *
 * ⚠ `money()` alone renders `€-18.50` — a hyphen, inside the number, where every other value on
 * this screen uses U+2212 and leads with it. `Num` is not usable here: it writes an inline
 * `font-size`, which is the trap `--num` sizing already records, and this is an SVG-adjacent label
 * rather than a measurement tile.
 */
function signedMoney(value: string): string {
  const negative = value.trim().startsWith('-');
  return `${negative ? '−' : ''}${money(value.replace(/^-/, ''))}`;
}

/** One lane's hover: what it is, when it ran, and how it ended. */
function timelineTitle(p: FleetPosition, mode: TimestampMode): string {
  const opened = clockOf(p.opened_at, mode);
  const closed = p.closed_at === null ? 'still open' : clockOf(p.closed_at, mode);
  const ended = p.exit_reason === null ? 'no exit reason yet' : `closed by ${p.exit_reason}`;
  const result =
    p.realised_gross === null ? 'nothing realised yet' : signedMoney(p.realised_gross);
  return [
    `${p.symbol} · ${p.side}`,
    `opened ${opened} · ${closed}`,
    `${ended} · ${p.bars_held} bars held`,
    result,
  ].join('\n');
}
